import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { IAvatar } from "./interfaces/avatar.interface";
import Axios from "axios";
import { endpoints } from "../../components/helpers/constants";
import { GLTFModelLoader } from "../../components/gltf/gltf-model-loader";
import { VisemeDebugPanel } from "../../components/phonemes/viseme-debug-panel";
import { Canvas, useThree } from "@react-three/fiber";
import Dictaphone from "../../components/speech-to-text/dictaphone";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ChatBox } from "../../components/chat-box/chat-box";
import {
  extractArabicPhonemes,
  extractEnglishPhonemes,
  getPhonemeMap,
} from "../../components/phonemes/phoneme-mapping";
import Preloader from "../../components/preloader/preloader";
import gsap from "gsap";
import { handleAnimationTransition } from "../../components/helpers/avatar-animations";
import { MorphScheme } from "../../components/helpers/detect-morph-scheme";
import { SCHEME_PROFILES } from "../../components/helpers/scheme-profiles";
import { isArabic } from "../../components/helpers/helpers";
import * as THREE from "three";
import {
  getSessionId,
  initNewSessionId,
  setSessionId,
  SESSION_ID_CHANGED_EVENT,
} from "../../components/common/storage";
import readNDJSONStream from "ndjson-readablestream";
import { AudioAligner } from "../../lib/audio-align";
import { AvtrWsClient } from "../../lib/avtr-ws-client";
import { streamAvatarSpeak } from "../../lib/avtr-speak-stream";
import { getAuthHeaders, fetchAsBlobUrl, clearAuthStorage, extractBaseLanguage, getStoredUserName } from "../../common/helpers";
import { resolveChatDialect } from "../../common/dialects";
import { detectChannelType, isMobileDevice } from "../../common/device";
import {
  applySystemLanguage,
  normalizeSystemLanguage,
  SystemLanguage,
} from "../../common/system-language";
import PersonalizationModal from "../../components/personalization/PersonalizationModal";
import { ProactiveOverlay } from "../../components/proactive-overlay/ProactiveOverlay";
import FaqButton from "../../components/FaqButton";
import { FeedbackModal } from "../../components/user-experience/FeedbackModal";
import { submitUserExperience } from "../../api/userExperienceService";
import { getVoicesForDialect, DialectVoiceDto } from "../../api/languageService";
import abgLogo from "../../assets/imgs/blue-logo.svg";
import {
  GripHorizontal,
  LogIn,
  LogOut,
  Move,
  PanelBottom,
  PanelLeft,
  PanelRight,
  RotateCcw,
  Settings,
} from "lucide-react";
import ConversationSidebar from "../../components/conversation-sidebar/ConversationSidebar";
import { useParams, useNavigate } from "react-router-dom";
import {
  ConversationSummary,
  fetchConversations,
  fetchConversationMessages,
  fetchSharedMessages,
  createOrUpdateSession,
  saveInteraction,
} from "../../api/conversationHistoryService";
import { isLoggedIn } from "../../common/helpers";

interface OutOfScopePayload {
  userId: string;
  question: string;
  llmAnswer: string;
}

interface AvatarOption {
  id: number | string;
  name: string;
  thumbnail: string;
  glbPath?: string;
  elevenLabsVoiceId: string;
  audioDemoPath: string;
  gender: number;
  description: string;
  isDefault: boolean;
  bgColor: string | null;
  themeId: number;
  personalityTags: string[];
  languageCodes: string[];
  languageNames: string[];
  avatarType?: "GLB" | "IMAGE";
  imagePath?: string;
  avtrAvatarId?: string;
  avtrBackgroundId?: string;
  inferenceRegistrationStatus?: string;
}

const CHOSEN_AVATAR_STORAGE_KEY = "chosenAvatar";
const MOBILE_LAYOUT_BREAKPOINT = 1024;
const COMPACT_LAYOUT_WIDTH_BREAKPOINT = 1200;
const LIVE_CHAT_MOBILE_STORAGE_KEY = "avatar-live-chat-layout-mobile-v1";
const LIVE_CHAT_DESKTOP_STORAGE_KEY = "avatar-live-chat-layout-desktop-v1";
const LIVE_CHAT_HEADER_OFFSET = 72;
const LIVE_CHAT_SIDE_GAP = 16;
const LIVE_CHAT_BOTTOM_GAP = 16;
const LIVE_CHAT_MIN_FLOATING_WIDTH = 280;
const LIVE_CHAT_MIN_FLOATING_HEIGHT = 200;
const LIVE_CHAT_EDGE_THRESHOLD = 72;
const isImageAvatar = (avatarLike: any): boolean =>
  String(avatarLike?.avatarType ?? "").toUpperCase() === "IMAGE";

const AUTO_DETECT_LANGUAGE = "auto";
const AUDIO_ALIGNMENT_MIME_TYPE = "audio/mpeg";
const PCM16_AUDIO_MIME_TYPE = "audio/wav";
/**
 * Audio formats the /generate-audio NDJSON stream can carry. The stream declares its own format in
 * a leading `{"audio_format": ...}` line; the ElevenLabs path sends no such line, so "mp3" is both
 * the default and the pre-existing behaviour. That is what makes `tts.glb-provider` a server-side
 * rollback: nothing here has to be redeployed to follow it back.
 */
const GLB_AUDIO_FORMAT_MP3 = "mp3";
const GLB_AUDIO_FORMAT_PCM16 = "pcm16";
const GLB_PCM_FALLBACK_SAMPLE_RATE = 16000;
const AUDIO_ALIGNMENT_THROTTLE_MS = 250;
const AUDIO_ALIGNMENT_MP3_BITRATE_BPS = 128000;
const AUDIO_ALIGNMENT_WORDS_PER_SECOND = 2.4;
const AUDIO_ALIGNMENT_LOOKAHEAD_SECONDS = 0.75;

type LiveChatMode = "left" | "right" | "bottom" | "floating";
type LiveChatResizeDirection = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";
type LiveChatDockPreview = Exclude<LiveChatMode, "floating"> | null;

interface LiveChatPanelLayout {
  mode: LiveChatMode;
  floating: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  docked: {
    leftWidth: number;
    rightWidth: number;
    bottomHeight: number;
  };
}

interface LiveChatPointerState {
  type: "drag" | "resize";
  pointerId: number;
  startX: number;
  startY: number;
  startLayout: LiveChatPanelLayout;
  offsetX?: number;
  offsetY?: number;
  direction?: LiveChatResizeDirection;
  element: HTMLElement | null;
}

const clampLiveChatValue = (value: number, min: number, max: number) => {
  if (Number.isNaN(value)) return min;
  if (max < min) return min;
  return Math.min(Math.max(value, min), max);
};

const getLiveChatViewport = () => {
  if (typeof window === "undefined") {
    return { width: 1440, height: 900 };
  }

  return {
    width: window.innerWidth,
    height: window.visualViewport?.height ?? window.innerHeight,
  };
};

const getLiveChatConstraints = (viewport: { width: number; height: number }) => {
  const maxWidth = Math.max(260, Math.floor(viewport.width * 0.9));
  const maxHeight = Math.max(
    LIVE_CHAT_MIN_FLOATING_HEIGHT,
    Math.floor((viewport.height - LIVE_CHAT_HEADER_OFFSET - LIVE_CHAT_BOTTOM_GAP) * 0.85)
  );

  return {
    minFloatingWidth: Math.min(LIVE_CHAT_MIN_FLOATING_WIDTH, maxWidth),
    maxFloatingWidth: maxWidth,
    minFloatingHeight: Math.min(LIVE_CHAT_MIN_FLOATING_HEIGHT, maxHeight),
    maxFloatingHeight: maxHeight,
    minDockWidth: Math.min(280, maxWidth),
    maxDockWidth: Math.max(280, Math.min(560, viewport.width - 64)),
    minBottomHeight: Math.min(220, maxHeight),
    maxBottomHeight: Math.max(220, Math.min(maxHeight, viewport.height - 120)),
  };
};

const createDefaultLiveChatLayout = (
  isMobileDefault: boolean,
  viewport = getLiveChatViewport()
): LiveChatPanelLayout => {
  const constraints = getLiveChatConstraints(viewport);
  const floatingWidth = clampLiveChatValue(
    Math.min(360, Math.floor(viewport.width * 0.34)),
    constraints.minFloatingWidth,
    constraints.maxFloatingWidth
  );
  const floatingHeight = clampLiveChatValue(
    Math.min(420, Math.floor((viewport.height - LIVE_CHAT_HEADER_OFFSET) * 0.58)),
    constraints.minFloatingHeight,
    constraints.maxFloatingHeight
  );
  const dockWidth = clampLiveChatValue(
    Math.min(360, Math.floor(viewport.width * 0.28)),
    constraints.minDockWidth,
    constraints.maxDockWidth
  );
  const bottomHeight = clampLiveChatValue(
    Math.min(360, Math.floor((viewport.height - LIVE_CHAT_HEADER_OFFSET) * 0.45)),
    constraints.minBottomHeight,
    constraints.maxBottomHeight
  );

  return {
    mode: isMobileDefault ? "bottom" : "right",
    floating: {
      x: clampLiveChatValue(
        viewport.width - floatingWidth - LIVE_CHAT_SIDE_GAP,
        LIVE_CHAT_SIDE_GAP,
        viewport.width - floatingWidth - LIVE_CHAT_SIDE_GAP
      ),
      y: clampLiveChatValue(
        LIVE_CHAT_HEADER_OFFSET + 24,
        LIVE_CHAT_HEADER_OFFSET,
        viewport.height - floatingHeight - LIVE_CHAT_BOTTOM_GAP
      ),
      width: floatingWidth,
      height: floatingHeight,
    },
    docked: {
      leftWidth: dockWidth,
      rightWidth: dockWidth,
      bottomHeight,
    },
  };
};

const normalizeLiveChatLayout = (
  layout: LiveChatPanelLayout,
  viewport = getLiveChatViewport()
): LiveChatPanelLayout => {
  const constraints = getLiveChatConstraints(viewport);
  const width = clampLiveChatValue(
    layout.floating.width,
    constraints.minFloatingWidth,
    constraints.maxFloatingWidth
  );
  const height = clampLiveChatValue(
    layout.floating.height,
    constraints.minFloatingHeight,
    constraints.maxFloatingHeight
  );

  return {
    mode: layout.mode,
    floating: {
      x: clampLiveChatValue(
        layout.floating.x,
        LIVE_CHAT_SIDE_GAP,
        Math.max(LIVE_CHAT_SIDE_GAP, viewport.width - width - LIVE_CHAT_SIDE_GAP)
      ),
      y: clampLiveChatValue(
        layout.floating.y,
        LIVE_CHAT_HEADER_OFFSET,
        Math.max(LIVE_CHAT_HEADER_OFFSET, viewport.height - height - LIVE_CHAT_BOTTOM_GAP)
      ),
      width,
      height,
    },
    docked: {
      leftWidth: clampLiveChatValue(
        layout.docked.leftWidth,
        constraints.minDockWidth,
        constraints.maxDockWidth
      ),
      rightWidth: clampLiveChatValue(
        layout.docked.rightWidth,
        constraints.minDockWidth,
        constraints.maxDockWidth
      ),
      bottomHeight: clampLiveChatValue(
        layout.docked.bottomHeight,
        constraints.minBottomHeight,
        constraints.maxBottomHeight
      ),
    },
  };
};

const uint8ChunksToBase64 = (chunks: Uint8Array[]): string => {
  let binary = "";

  chunks.forEach((chunk) => {
    for (let i = 0; i < chunk.length; i++) {
      binary += String.fromCharCode(chunk[i]);
    }
  });

  return btoa(binary);
};

const estimateMp3DurationSeconds = (chunks: Uint8Array[]): number => {
  const totalBytes = chunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  return (totalBytes * 8) / AUDIO_ALIGNMENT_MP3_BITRATE_BPS;
};

const estimatePcm16DurationSeconds = (
  chunks: Uint8Array[],
  sampleRate: number
): number => {
  if (!sampleRate) return 0;
  const totalBytes = chunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  return totalBytes / (sampleRate * 2); // 16-bit mono
};

/**
 * Wrap raw 16-bit mono PCM in a WAV container so an <audio> element can play it.
 *
 * The PCM arrives headerless, and we only learn its true length once the stream ends — which is
 * fine, because this is only ever called at the end. Building the header here rather than on the
 * server is what keeps every interim buffer pure PCM, which is exactly what the aligner wants.
 */
const buildWavBlob = (chunks: Uint8Array[], sampleRate: number): Blob => {
  const totalBytes = chunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  const dataBytes = totalBytes - (totalBytes % 2); // whole samples only
  const header = new ArrayBuffer(44);
  const view = new DataView(header);
  const ascii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };

  ascii(0, "RIFF");
  view.setUint32(4, 36 + dataBytes, true);
  ascii(8, "WAVE");
  ascii(12, "fmt ");
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // 1 = uncompressed PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  ascii(36, "data");
  view.setUint32(40, dataBytes, true);

  return new Blob([header, ...(chunks as BlobPart[])], { type: PCM16_AUDIO_MIME_TYPE });
};

const selectTranscriptPrefixForAudio = (
  transcript: string,
  audioDurationSeconds: number
): string => {
  const words = transcript.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return transcript;

  const targetWords = Math.max(
    1,
    Math.ceil(
      (audioDurationSeconds + AUDIO_ALIGNMENT_LOOKAHEAD_SECONDS) *
      AUDIO_ALIGNMENT_WORDS_PER_SECOND
    )
  );

  return words.slice(0, Math.min(words.length, targetWords)).join(" ");
};

const isLikelyIosWebKit = () => {
  if (typeof navigator === "undefined") return false;

  const userAgent = navigator.userAgent || "";
  const platform = navigator.platform || "";
  const touchPoints = navigator.maxTouchPoints || 0;
  const isAppleMobileDevice =
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (platform === "MacIntel" && touchPoints > 1);
  const isWebKitBrowser =
    /WebKit/i.test(userAgent) &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(userAgent);

  return isAppleMobileDevice && isWebKitBrowser;
};

const canUseStreamedAudioMse = () => {
  if (typeof MediaSource === "undefined") return false;
  if (isLikelyIosWebKit()) return false;
  // Mobile/tablet browsers report `audio/mpeg` as MSE-supported but starve the element after the
  // first appended chunk (playback stops ~1s in and never resumes). Buffer the whole answer and
  // play a plain blob there instead — costs first-audio latency, but it actually plays, and the
  // aligner gets the complete mp3 up front so lip-sync is driven from a full alignment.
  if (detectChannelType() !== "desktop") return false;

  try {
    return typeof MediaSource.isTypeSupported === "function"
      ? MediaSource.isTypeSupported(AUDIO_ALIGNMENT_MIME_TYPE)
      : true;
  } catch {
    return false;
  }
};

const reportOutOfScope = async (payload: OutOfScopePayload) => {
  try {
    const response = await Axios.post(
      `${process.env.REACT_APP_BASE_URL}api/out-of-scope`,
      payload
    );
    if (response.status !== 200) {
      console.warn(`Out-of-scope reporting failed with status: ${response.status}`);
    }
  } catch (err) {
    console.error("Out-of-scope reporting error:", err);
  }
};

const preloadVideo = (url: string, timeoutMs = 15000): Promise<void> => {
  if (!url) return Promise.resolve();

  const fetchPromise = fetchAsBlobUrl(url)
    .then(() => undefined)
    .catch((e) => {
      console.warn("⚠️ Failed to preload video:", url, e);
    });

  if (timeoutMs <= 0) return fetchPromise;

  return Promise.race([
    fetchPromise,
    new Promise<void>((resolve) =>
      window.setTimeout(() => {
        console.warn("⏱ Preload timeout, continuing anyway:", url);
        resolve();
      }, timeoutMs)
    ),
  ]);
};

const preloadVideoNoTimeout = (url: string): Promise<void> => {
  return preloadVideo(url, 0);
};

const resolveSpeakerLanguage = (
  selectedLanguage: string | undefined | null,
  fallbackLanguage: string
) =>
  selectedLanguage && selectedLanguage !== AUTO_DETECT_LANGUAGE
    ? selectedLanguage
    : fallbackLanguage;

/**
 * Phones and tablets always transcribe with Auto Detect, whatever is stored. A fixed speaker
 * language routes STT through the Web Speech API, which mobile engines support unevenly and which
 * latches itself off for the whole page after a single permission error; Auto Detect uses the
 * Whisper socket instead, which behaves the same everywhere. A value saved on desktop and synced
 * to a phone must not drag it back onto the fragile path, so this is applied on read as well as on
 * save — the settings UI hides the other options to match.
 */
const applySpeakerLanguagePolicy = (selectedLanguage: string): string =>
  isMobileDevice() ? AUTO_DETECT_LANGUAGE : selectedLanguage;

const getStoredChosenAvatarId = (): string => {
  try {
    const savedChosenAvatar = localStorage.getItem(CHOSEN_AVATAR_STORAGE_KEY);
    if (!savedChosenAvatar) return "";

    const parsedChosenAvatar = JSON.parse(savedChosenAvatar);
    return parsedChosenAvatar?.id ? String(parsedChosenAvatar.id) : "";
  } catch (error) {
    console.error("Failed to parse chosenAvatar from localStorage:", error);
    return "";
  }
};

const getUiCopy = (systemLanguage: SystemLanguage) =>
  systemLanguage === "ar"
    ? {
      settings: "الإعدادات",
      logout: "تسجيل الخروج",
      signIn: "تسجيل الدخول",
      closeChat: "إغلاق الدردشة",
      openChat: "فتح الدردشة",
      avatarListening: "الأفاتار يستمع...",
      videoPaused: "تم إيقاف الفيديو مؤقتًا",
      videoResumed: "تم استئناف الفيديو",
      heroTitle: "Mynd - ذكاء اصطناعي يفكر معك",
      poweredBy: "مدعوم من",
      accessRestrictedTitle: "الوصول مقيد",
      accessRestrictedBody: "هذه الخدمة غير متاحة على جهازك الحالي.",
    }
    : {
      settings: "Settings",
      logout: "Logout",
      signIn: "Sign In",
      closeChat: "Close chat",
      openChat: "Open chat",
      avatarListening: "Avatar is listening...",
      videoPaused: "Video Paused",
      videoResumed: "Video Resumed",
      heroTitle: "Mynd - AI That Thinks With You",
      poweredBy: "POWERED BY",
      accessRestrictedTitle: "Access Restricted",
      accessRestrictedBody: "This service is not available on your current device.",
    };

const CameraUpdater = ({ fov }: { fov?: number }) => {
  const { camera } = useThree();

  useEffect(() => {
    if (fov && fov > 0 && camera instanceof THREE.PerspectiveCamera) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }
  }, [fov, camera]);

  return null;
};

/**
 * Whether feedback was already submitted for the conversation that is active right now.
 * Guards the missing-session case so the latch can never key off `feedback_undefined`
 * and hide the button on a freshly loaded page.
 */
const hasSubmittedFeedback = () => {
  try {
    const sessionId = getSessionId();
    return !!sessionId && localStorage.getItem(`feedback_${sessionId}`) === "true";
  } catch {
    return false;
  }
};

export function Avatar() {
  const { token: shareToken, sessionId: urlSessionId } = useParams<{ token?: string; sessionId?: string }>();
  const navigate = useNavigate();
  const isReadOnly = !!shareToken;

  // The route param, readable from code that runs after awaits (the settings bootstrap), where the
  // value captured in the effect's closure can be stale.
  const routeSessionIdRef = useRef<string | undefined>(urlSessionId);
  routeSessionIdRef.current = urlSessionId;
  // A session id is minted once per page load, and only when no conversation is open.
  const sessionMintedRef = useRef(false);
  // Set to the session id right before this page navigates to /chat/:sessionId itself, so the
  // route effect can tell its own navigation apart from the user opening a conversation.
  const pendingNavigationSessionIdRef = useRef<string | null>(null);

  const [language, setLanguage] = useState("en-US");
  const [avatarSettings, setAvatarSettings] = useState<any | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  const [avatar, setAvatar] = useState({} as IAvatar);
  const [avatarTheme, setAvatarTheme] = useState({}) as any;
  // Theme image/video assets are public static files, so the browser loads them straight
  // from their URL. (Fetching them to a blob first went through CORS + auth and failed.)
  const avatarThemeAssetBlob: string | null = avatarTheme.linkUrl || null;
  const [avatarOptions, setAvatarOptions] = useState<AvatarOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [glbReady, setGlbReady] = useState(false);
  const [showProactiveOverlay, setShowProactiveOverlay] = useState(false);
  const proactiveStartedRef = useRef(false);
  const [avatarTalking, setAvatarTalking] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [messages, setMessages] = useState<[]>([]) as any;
  const [avatarActions, setAvatarActions] = useState() as any;
  const [avatarAudio, setAvatarAudio] = useState() as any;
  const [morphTarget, setMorphTarget] = useState() as any;
  const [morphScheme, setMorphScheme] = useState<MorphScheme>("LEGACY");
  const [animationFrameId, setAnimationFrameId] = useState() as any;
  const [returnedText, setReturnedText] = useState("");
  const alignmentsRef = useRef<any[]>([]);
  const alignmentsRevisionRef = useRef(0);
  const [hasPlaylist, setHasPlaylist] = useState<boolean | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  // Picture-mode talking-head <video> (AVTR). Mutable so the ref callback can assign it.
  const pictureVideoRef = useRef<HTMLVideoElement | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const clearMessagesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const [everythingLoaded, setEverythingLoaded] = useState(false);
  const [isAvatarListeningIndicator, setIsAvatarListeningIndicator] =
    useState(false);

  const [videoStatus, setVideoStatus] = useState<
    "playing" | "paused" | "resumed"
  >("playing");
  const videoStatusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [playlists, setPlaylists] = useState<any[]>([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [nextPlaylists, setNextPlaylists] = useState<any[]>([]);

  const [channelBlocked, setChannelBlocked] = useState(false);
  const [showChatBox, setShowChatBox] = useState(false);
  const [isChatSidebarOpen, setIsChatSidebarOpen] = useState(true);
  const [idleTimeoutDuration, setIdleTimeoutDuration] = useState(10000);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(hasSubmittedFeedback);

  // The feedback latch is per conversation, but this page never remounts when the user
  // switches or starts one, so the initial read would stay frozen for the rest of the
  // page's life: one submission hid the button in every later conversation. Re-read it
  // whenever the active session id moves.
  useEffect(() => {
    const syncFeedbackSubmitted = () => setFeedbackSubmitted(hasSubmittedFeedback());
    window.addEventListener(SESSION_ID_CHANGED_EVENT, syncFeedbackSubmitted);
    return () => window.removeEventListener(SESSION_ID_CHANGED_EVENT, syncFeedbackSubmitted);
  }, []);
  const [personalizationForm, setPersonalizationForm] = useState({
    speakerLanguage: "",
    avatarLanguage: "",
    avatarVoiceId: "",
    selectedAvatarId: "",
    systemLanguage: "en",
    tapToTalk: true,
    floatingChatEnabled: false,
  });

  // Conversation sidebar state
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentConversationSessionId, setCurrentConversationSessionId] = useState<string | null>(null);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const [isLoadingMoreConversations, setIsLoadingMoreConversations] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationPage, setConversationPage] = useState(0);
  const userAuthenticated = isLoggedIn();
  const userId = localStorage.getItem("identity") || "";

  const isFloatingChatEnabled = personalizationForm.floatingChatEnabled;

  const [sessionResetCounter, setSessionResetCounter] = useState(0);
  const tapToTalk = settingsLoaded ? personalizationForm.tapToTalk : false;
  const systemLanguage = normalizeSystemLanguage(
    personalizationForm.systemLanguage
  );
  const isRtl = systemLanguage === "ar";
  const uiCopy = getUiCopy(systemLanguage);
  const [isMobileLayout, setIsMobileLayout] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth < MOBILE_LAYOUT_BREAKPOINT;
  });
  const [isCompactViewport, setIsCompactViewport] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.innerWidth < COMPACT_LAYOUT_WIDTH_BREAKPOINT;
  });
  const shouldDockSidebarBottom = isMobileLayout || isCompactViewport;
  const liveChatStorageKey = shouldDockSidebarBottom
    ? LIVE_CHAT_MOBILE_STORAGE_KEY
    : LIVE_CHAT_DESKTOP_STORAGE_KEY;
  const [liveChatPanelLayout, setLiveChatPanelLayout] = useState<LiveChatPanelLayout>(
    () => normalizeLiveChatLayout(createDefaultLiveChatLayout(false))
  );
  const [liveChatDockPreview, setLiveChatDockPreview] =
    useState<LiveChatDockPreview>(null);
  const liveChatPanelRef = useRef<HTMLDivElement | null>(null);
  const liveChatLayoutRef = useRef(liveChatPanelLayout);
  const liveChatDockPreviewRef = useRef<LiveChatDockPreview>(null);
  const liveChatInteractionRef = useRef<LiveChatPointerState | null>(null);
  const liveChatAnimationFrameRef = useRef<number | null>(null);
  const isLiveChatOpen = isChatSidebarOpen;
  const isBottomChatSheetOpen =
    liveChatPanelLayout.mode === "bottom" && isChatSidebarOpen;
  const shouldShowLiveChatToggle = true;
  const shouldRenderLiveChat =
    !isFloatingChatEnabled ||
    avatarTalking ||
    avatarLoading ||
    isLiveChatOpen ||
    showChatBox ||
    messages.length > 0;
  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleResize = () => {
      setIsMobileLayout(window.innerWidth < MOBILE_LAYOUT_BREAKPOINT);
      setIsCompactViewport(window.innerWidth < COMPACT_LAYOUT_WIDTH_BREAKPOINT);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const body = document.body;
    body.style.setProperty(
      "--live-chat-bottom-sheet-height",
      `${liveChatPanelLayout.docked.bottomHeight}px`
    );

    if (isBottomChatSheetOpen) {
      body.classList.add("bottom-chat-sheet-open");
    } else {
      body.classList.remove("bottom-chat-sheet-open");
    }

    return () => {
      body.classList.remove("bottom-chat-sheet-open");
      body.style.removeProperty("--live-chat-bottom-sheet-height");
    };
  }, [isBottomChatSheetOpen, liveChatPanelLayout.docked.bottomHeight]);

  const applyLiveChatLayout = useCallback((next: LiveChatPanelLayout) => {
    const normalized = normalizeLiveChatLayout(next);
    liveChatLayoutRef.current = normalized;

    if (liveChatAnimationFrameRef.current !== null) {
      cancelAnimationFrame(liveChatAnimationFrameRef.current);
    }

    liveChatAnimationFrameRef.current = window.requestAnimationFrame(() => {
      setLiveChatPanelLayout(normalized);
      liveChatAnimationFrameRef.current = null;
    });
  }, []);

  const resetLiveChatLayout = useCallback(() => {
    const defaults = normalizeLiveChatLayout(
      createDefaultLiveChatLayout(shouldDockSidebarBottom)
    );
    setLiveChatDockPreview(null);
    applyLiveChatLayout(defaults);
  }, [applyLiveChatLayout, shouldDockSidebarBottom]);

  useEffect(() => {
    liveChatLayoutRef.current = liveChatPanelLayout;
  }, [liveChatPanelLayout]);

  useEffect(() => {
    liveChatDockPreviewRef.current = liveChatDockPreview;
  }, [liveChatDockPreview]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(liveChatStorageKey);
      if (raw) {
        const stored = JSON.parse(raw) as LiveChatPanelLayout;
        const normalized = normalizeLiveChatLayout(stored);
        setLiveChatPanelLayout(normalized);
        liveChatLayoutRef.current = normalized;
        return;
      }
    } catch {
      // ignore invalid persisted layout
    }

    const defaults = normalizeLiveChatLayout(
      createDefaultLiveChatLayout(shouldDockSidebarBottom)
    );
    setLiveChatPanelLayout(defaults);
    liveChatLayoutRef.current = defaults;
  }, [liveChatStorageKey, shouldDockSidebarBottom]);

  useEffect(() => {
    try {
      localStorage.setItem(liveChatStorageKey, JSON.stringify(liveChatPanelLayout));
    } catch {
      // ignore storage write failures
    }
  }, [liveChatPanelLayout, liveChatStorageKey]);

  useEffect(() => {
    const handleViewportResize = () => {
      const normalized = normalizeLiveChatLayout(liveChatLayoutRef.current);
      liveChatLayoutRef.current = normalized;
      setLiveChatPanelLayout(normalized);
    };

    window.addEventListener("resize", handleViewportResize);
    window.visualViewport?.addEventListener("resize", handleViewportResize);

    return () => {
      window.removeEventListener("resize", handleViewportResize);
      window.visualViewport?.removeEventListener("resize", handleViewportResize);
    };
  }, []);

  const endLiveChatInteraction = useCallback(
    (commitDockPreview: boolean) => {
      const active = liveChatInteractionRef.current;
      if (!active) return;

      if (active.element) {
        try {
          active.element.releasePointerCapture(active.pointerId);
        } catch {
          // noop
        }
      }

      if (commitDockPreview && liveChatDockPreviewRef.current) {
        applyLiveChatLayout({
          ...liveChatLayoutRef.current,
          mode: liveChatDockPreviewRef.current,
        });
      }

      liveChatInteractionRef.current = null;
      setLiveChatDockPreview(null);
    },
    [applyLiveChatLayout]
  );

  useEffect(() => {
    const handlePointerMove = (event: PointerEvent) => {
      const active = liveChatInteractionRef.current;
      if (!active || active.pointerId !== event.pointerId) return;

      const viewport = getLiveChatViewport();
      const constraints = getLiveChatConstraints(viewport);
      const dx = event.clientX - active.startX;
      const dy = event.clientY - active.startY;

      if (active.type === "drag") {
        const preview =
          event.clientY >= viewport.height - LIVE_CHAT_EDGE_THRESHOLD
            ? "bottom"
            : event.clientX <= LIVE_CHAT_EDGE_THRESHOLD
              ? "left"
              : event.clientX >= viewport.width - LIVE_CHAT_EDGE_THRESHOLD
                ? "right"
                : null;

        if (preview !== liveChatDockPreviewRef.current) {
          liveChatDockPreviewRef.current = preview;
          setLiveChatDockPreview(preview);
        }
        applyLiveChatLayout({
          ...active.startLayout,
          mode: "floating",
          floating: {
            ...active.startLayout.floating,
            x: clampLiveChatValue(
              event.clientX - (active.offsetX ?? 0),
              LIVE_CHAT_SIDE_GAP,
              viewport.width -
                active.startLayout.floating.width -
                LIVE_CHAT_SIDE_GAP
            ),
            y: clampLiveChatValue(
              event.clientY - (active.offsetY ?? 0),
              LIVE_CHAT_HEADER_OFFSET,
              viewport.height -
                active.startLayout.floating.height -
                LIVE_CHAT_BOTTOM_GAP
            ),
          },
        });
        return;
      }

      if (!active.direction) return;

      const start = active.startLayout;
      if (start.mode === "floating") {
        let nextX = start.floating.x;
        let nextY = start.floating.y;
        let nextWidth = start.floating.width;
        let nextHeight = start.floating.height;

        if (active.direction.includes("e")) {
          nextWidth = clampLiveChatValue(
            start.floating.width + dx,
            constraints.minFloatingWidth,
            constraints.maxFloatingWidth
          );
        }
        if (active.direction.includes("s")) {
          nextHeight = clampLiveChatValue(
            start.floating.height + dy,
            constraints.minFloatingHeight,
            constraints.maxFloatingHeight
          );
        }
        if (active.direction.includes("w")) {
          const proposedWidth = clampLiveChatValue(
            start.floating.width - dx,
            constraints.minFloatingWidth,
            constraints.maxFloatingWidth
          );
          nextX = clampLiveChatValue(
            start.floating.x + (start.floating.width - proposedWidth),
            LIVE_CHAT_SIDE_GAP,
            viewport.width - proposedWidth - LIVE_CHAT_SIDE_GAP
          );
          nextWidth = proposedWidth;
        }
        if (active.direction.includes("n")) {
          const proposedHeight = clampLiveChatValue(
            start.floating.height - dy,
            constraints.minFloatingHeight,
            constraints.maxFloatingHeight
          );
          nextY = clampLiveChatValue(
            start.floating.y + (start.floating.height - proposedHeight),
            LIVE_CHAT_HEADER_OFFSET,
            viewport.height - proposedHeight - LIVE_CHAT_BOTTOM_GAP
          );
          nextHeight = proposedHeight;
        }

        applyLiveChatLayout({
          ...start,
          floating: {
            x: nextX,
            y: nextY,
            width: nextWidth,
            height: nextHeight,
          },
        });
        return;
      }

      if (start.mode === "left") {
        applyLiveChatLayout({
          ...start,
          docked: {
            ...start.docked,
            leftWidth: clampLiveChatValue(
              event.clientX - LIVE_CHAT_SIDE_GAP,
              constraints.minDockWidth,
              constraints.maxDockWidth
            ),
          },
        });
        return;
      }

      if (start.mode === "right") {
        applyLiveChatLayout({
          ...start,
          docked: {
            ...start.docked,
            rightWidth: clampLiveChatValue(
              viewport.width - event.clientX - LIVE_CHAT_SIDE_GAP,
              constraints.minDockWidth,
              constraints.maxDockWidth
            ),
          },
        });
        return;
      }

      applyLiveChatLayout({
        ...start,
        docked: {
          ...start.docked,
          bottomHeight: clampLiveChatValue(
            viewport.height - event.clientY - LIVE_CHAT_BOTTOM_GAP,
            constraints.minBottomHeight,
            constraints.maxBottomHeight
          ),
        },
      });
    };

    const handlePointerUp = (event: PointerEvent) => {
      if (liveChatInteractionRef.current?.pointerId !== event.pointerId) return;
      endLiveChatInteraction(true);
    };

    const handlePointerCancel = (event: PointerEvent) => {
      if (liveChatInteractionRef.current?.pointerId !== event.pointerId) return;
      endLiveChatInteraction(false);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerCancel);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerCancel);
    };
  }, [applyLiveChatLayout, endLiveChatInteraction]);

  useEffect(() => {
    return () => {
      if (liveChatAnimationFrameRef.current !== null) {
        cancelAnimationFrame(liveChatAnimationFrameRef.current);
      }
    };
  }, []);

  const beginLiveChatDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!liveChatPanelRef.current) return;

      event.preventDefault();
      event.stopPropagation();

      const rect = liveChatPanelRef.current.getBoundingClientRect();
      const nextLayout = normalizeLiveChatLayout({
        ...liveChatLayoutRef.current,
        mode: "floating",
        floating: {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height,
        },
      });

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // noop
      }

      liveChatInteractionRef.current = {
        type: "drag",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startLayout: nextLayout,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        element: event.currentTarget,
      };

      applyLiveChatLayout(nextLayout);
    },
    [applyLiveChatLayout]
  );

  const beginLiveChatResize = useCallback(
    (event: React.PointerEvent<HTMLElement>, direction: LiveChatResizeDirection) => {
      event.preventDefault();
      event.stopPropagation();

      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        // noop
      }

      liveChatInteractionRef.current = {
        type: "resize",
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startLayout: liveChatLayoutRef.current,
        direction,
        element: event.currentTarget,
      };
    },
    []
  );

  const setLiveChatMode = useCallback(
    (mode: LiveChatMode) => {
      applyLiveChatLayout({
        ...liveChatLayoutRef.current,
        mode,
      });
      setIsChatSidebarOpen(true);
    },
    [applyLiveChatLayout]
  );

  const liveChatPanelStyle = useMemo<CSSProperties>(() => {
    const mode = liveChatPanelLayout.mode;
    const shouldOverlayMicOnMobile =
      isMobileLayout &&
      isLiveChatOpen &&
      (mode === "left" || mode === "right");
    const common: CSSProperties = {
      position: "fixed",
      // Keep the docked mobile side panel above the mic pill only while it is open.
      zIndex: shouldOverlayMicOnMobile ? 100000 : 9999,
      overflow: "hidden",
      transition:
        liveChatInteractionRef.current
          ? "none"
          : "transform 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease, left 320ms cubic-bezier(0.22, 1, 0.36, 1), right 320ms cubic-bezier(0.22, 1, 0.36, 1), top 320ms cubic-bezier(0.22, 1, 0.36, 1), bottom 320ms cubic-bezier(0.22, 1, 0.36, 1), width 320ms cubic-bezier(0.22, 1, 0.36, 1), height 320ms cubic-bezier(0.22, 1, 0.36, 1)",
      opacity: isLiveChatOpen ? 1 : 0,
      pointerEvents: isLiveChatOpen ? "auto" : "none",
      willChange: liveChatInteractionRef.current
        ? "transform, left, top, width, height"
        : "auto",
    };

    if (mode === "left") {
      return {
        ...common,
        top: 0,
        left: 0,
        width: liveChatPanelLayout.docked.leftWidth,
        height: "100dvh",
        transform: isLiveChatOpen ? "translateX(0)" : "translateX(-100%)",
      };
    }

    if (mode === "right") {
      return {
        ...common,
        top: 0,
        right: 0,
        width: liveChatPanelLayout.docked.rightWidth,
        height: "100dvh",
        transform: isLiveChatOpen ? "translateX(0)" : "translateX(100%)",
      };
    }

    if (mode === "bottom") {
      return {
        ...common,
        left: 0,
        right: 0,
        width: "100%",
        maxWidth: "100%",
        top: "auto",
        bottom: 0,
        height: liveChatPanelLayout.docked.bottomHeight,
        maxHeight: "85dvh",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        borderBottomLeftRadius: shouldDockSidebarBottom ? 0 : 24,
        borderBottomRightRadius: shouldDockSidebarBottom ? 0 : 24,
        boxShadow: "0 -16px 40px rgba(15, 23, 42, 0.28)",
        transform: isLiveChatOpen ? "translateY(0)" : "translateY(100%)",
      };
    }

    return {
      ...common,
      left: liveChatPanelLayout.floating.x,
      top: liveChatPanelLayout.floating.y,
      width: liveChatPanelLayout.floating.width,
      height: liveChatPanelLayout.floating.height,
      maxWidth: "90vw",
      maxHeight: "85dvh",
      borderRadius: 24,
      boxShadow: "0 20px 60px rgba(15, 23, 42, 0.22)",
      transform: isLiveChatOpen ? "translateY(0)" : "translateY(12px)",
    };
  }, [isLiveChatOpen, isMobileLayout, liveChatPanelLayout, shouldDockSidebarBottom]);

  const liveChatToggleComputedStyle = useMemo<CSSProperties>(() => {
    if (liveChatPanelLayout.mode === "bottom") {
      return {
        top: "auto",
        bottom: isLiveChatOpen
          ? `calc(${liveChatPanelLayout.docked.bottomHeight}px + 24px + env(safe-area-inset-bottom))`
          : "136px",
        transform: "none",
        left: isRtl ? "16px" : "auto",
        right: isRtl ? "auto" : "16px",
      };
    }

    return {
      top: "50%",
      bottom: "auto",
      transform: "translateY(-50%)",
      left:
        liveChatPanelLayout.mode === "left"
          ? isLiveChatOpen
            ? `${liveChatPanelLayout.docked.leftWidth - 20}px`
            : "0px"
          : "auto",
      right:
        liveChatPanelLayout.mode === "right"
          ? isLiveChatOpen
            ? `${liveChatPanelLayout.docked.rightWidth - 20}px`
            : "0px"
          : "16px",
    };
  }, [isLiveChatOpen, isRtl, liveChatPanelLayout]);

  const liveChatDockPreviewStyle = useMemo<CSSProperties | undefined>(() => {
    if (!liveChatDockPreview) return undefined;
    if (liveChatDockPreview === "left") {
      return {
        position: "fixed",
        top: LIVE_CHAT_HEADER_OFFSET,
        left: LIVE_CHAT_SIDE_GAP,
        width: liveChatPanelLayout.docked.leftWidth,
        height: `calc(100dvh - ${LIVE_CHAT_HEADER_OFFSET + LIVE_CHAT_BOTTOM_GAP}px - env(safe-area-inset-bottom))`,
      };
    }
    if (liveChatDockPreview === "right") {
      return {
        position: "fixed",
        top: LIVE_CHAT_HEADER_OFFSET,
        right: LIVE_CHAT_SIDE_GAP,
        width: liveChatPanelLayout.docked.rightWidth,
        height: `calc(100dvh - ${LIVE_CHAT_HEADER_OFFSET + LIVE_CHAT_BOTTOM_GAP}px - env(safe-area-inset-bottom))`,
      };
    }
    return {
      position: "fixed",
      left: 0,
      right: 0,
      bottom: 0,
      height: liveChatPanelLayout.docked.bottomHeight,
    };
  }, [liveChatDockPreview, liveChatPanelLayout]);

  const liveChatContentTopInset =
    liveChatPanelLayout.mode === "bottom"
      ? isMobileLayout
        ? 88
        : 64
      : isMobileLayout
        ? 72
        : 56;

  const liveChatContentBottomInset =
    liveChatPanelLayout.mode === "bottom"
      ? isMobileLayout
        ? 92
        : 84
      : isMobileLayout
        ? 84
        : 72;

  const renderLiveChatResizeHandle = (
    direction: LiveChatResizeDirection,
    style: CSSProperties
  ) => (
    <div
      key={direction}
      role="presentation"
      onPointerDown={(event) => beginLiveChatResize(event, direction)}
      style={{
        position: "absolute",
        zIndex: 2,
        touchAction: "none",
        ...style,
      }}
    />
  );

  const renderLiveChatPanel = () => (
    <>
      {liveChatDockPreviewStyle && (
        <div
          aria-hidden="true"
          style={{
            ...liveChatDockPreviewStyle,
            zIndex: 69,
            pointerEvents: "none",
            borderRadius: 24,
            border: "1px solid rgba(51,51,255,0.28)",
            background: "rgba(51,51,255,0.12)",
          }}
        />
      )}

      {shouldShowLiveChatToggle && (
        <button
          className={`chat-sidebar-toggle ${isChatSidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
          onClick={() => setIsChatSidebarOpen((prev) => !prev)}
          style={liveChatToggleComputedStyle}
          aria-label={isChatSidebarOpen ? uiCopy.closeChat : uiCopy.openChat}
        >
          {liveChatPanelLayout.mode === "bottom" ? (
            isChatSidebarOpen ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />
          ) : isChatSidebarOpen ? (
            liveChatPanelLayout.mode === "left" ? <ChevronLeftIcon /> : <ChevronRightIcon />
          ) : liveChatPanelLayout.mode === "left" ? (
            <ChevronRightIcon />
          ) : (
            <ChevronLeftIcon />
          )}
        </button>
      )}

      {liveChatPanelLayout.mode === "bottom" && isLiveChatOpen && (
        <div className="chat-sidebar-backdrop" onClick={() => setIsChatSidebarOpen(false)} />
      )}

      {shouldRenderLiveChat && (
        <div ref={liveChatPanelRef} className="chat-sidebar" style={liveChatPanelStyle}>
          {liveChatPanelLayout.mode === "floating" && (
            <>
              {renderLiveChatResizeHandle("n", {
                top: -4,
                left: 14,
                right: 14,
                height: 10,
                cursor: "row-resize",
              })}
              {renderLiveChatResizeHandle("s", {
                bottom: -4,
                left: 14,
                right: 14,
                height: 10,
                cursor: "row-resize",
              })}
              {renderLiveChatResizeHandle("e", {
                top: 14,
                right: -4,
                bottom: 14,
                width: 10,
                cursor: "col-resize",
              })}
              {renderLiveChatResizeHandle("w", {
                top: 14,
                left: -4,
                bottom: 14,
                width: 10,
                cursor: "col-resize",
              })}
              {renderLiveChatResizeHandle("nw", {
                top: -4,
                left: -4,
                width: 16,
                height: 16,
                cursor: "nwse-resize",
              })}
              {renderLiveChatResizeHandle("ne", {
                top: -4,
                right: -4,
                width: 16,
                height: 16,
                cursor: "nesw-resize",
              })}
              {renderLiveChatResizeHandle("sw", {
                bottom: -4,
                left: -4,
                width: 16,
                height: 16,
                cursor: "nesw-resize",
              })}
              {renderLiveChatResizeHandle("se", {
                bottom: -4,
                right: -4,
                width: 16,
                height: 16,
                cursor: "nwse-resize",
              })}
            </>
          )}

          {liveChatPanelLayout.mode === "left" && renderLiveChatResizeHandle("e", {
            top: 0,
            right: 0,
            height: "100%",
            width: 10,
            cursor: "col-resize",
          })}
          {liveChatPanelLayout.mode === "right" && renderLiveChatResizeHandle("w", {
            top: 0,
            left: 0,
            height: "100%",
            width: 10,
            cursor: "col-resize",
          })}
          {liveChatPanelLayout.mode === "bottom" && renderLiveChatResizeHandle("n", {
            top: 0,
            left: 0,
            width: "100%",
            height: 10,
            cursor: "row-resize",
          })}

          <div
            className="absolute z-[10001] flex items-center gap-2"
            style={{
              top: 12,
              left: liveChatPanelLayout.mode === "right" ? 12 : "auto",
              right: 12,
              maxWidth: "calc(100% - 24px)",
            }}
          >
            <button
              type="button"
              onPointerDown={beginLiveChatDrag}
              className="flex min-w-0 items-center gap-2 rounded-full border border-white/20 bg-black/30 px-3 py-2 text-white backdrop-blur-md"
              style={{ cursor: liveChatPanelLayout.mode === "floating" ? "grab" : "move", touchAction: "none" }}
              aria-label={systemLanguage === "ar" ? "سحب الدردشة" : "Drag chat"}
              title={systemLanguage === "ar" ? "سحب الدردشة" : "Drag chat"}
            >
              <GripHorizontal size={16} />
            </button>

            <div className="flex items-center gap-1 rounded-full border border-white/20 bg-black/30 p-1 text-white backdrop-blur-md">
              <button type="button" onClick={() => setLiveChatMode("left")} className="rounded-full p-2 text-white hover:bg-white/10" aria-label="Dock left" title="Dock left">
                <PanelLeft size={16} />
              </button>
              <button type="button" onClick={() => setLiveChatMode("right")} className="rounded-full p-2 text-white hover:bg-white/10" aria-label="Dock right" title="Dock right">
                <PanelRight size={16} />
              </button>
              <button type="button" onClick={() => setLiveChatMode("bottom")} className="rounded-full p-2 text-white hover:bg-white/10" aria-label="Dock bottom" title="Dock bottom">
                <PanelBottom size={16} />
              </button>
              <button type="button" onClick={() => setLiveChatMode("floating")} className="rounded-full p-2 text-white hover:bg-white/10" aria-label="Floating mode" title="Floating mode">
                <Move size={16} />
              </button>
              <button type="button" onClick={resetLiveChatLayout} className="rounded-full p-2 text-white hover:bg-white/10" aria-label="Reset position and size" title="Reset position and size">
                <RotateCcw size={16} />
              </button>
            </div>
          </div>

          <ChatBox
            language={language}
            onMessageSend={handleMessageSend}
            backgroundColor={0}
            messages={messages}
            avatar={avatar}
            isLoading={avatarLoading}
            systemLanguage={systemLanguage}
            readOnly={isReadOnly}
            textingEnabled={avatarSettings?.textingEnabled !== false}
            interactionDisabled={avtrInteractionDisabled}
            floatingMode={liveChatPanelLayout.mode === "floating"}
            contentTopInset={liveChatContentTopInset}
            contentBottomInset={liveChatContentBottomInset}
          />
        </div>
      )}
    </>
  );

  // Dev-only viseme tuning overlay, opt-in via ?visemeDebug=1. No effect otherwise.
  const showVisemeDebug = useMemo(
    () =>
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("visemeDebug") === "1",
    []
  );

  // Picture mode renders the AVTR photoreal avatar instead of the 3D GLTF one, reusing the same
  // conversation flow. Selected via the settings modal (the "Noura" option) and persisted through
  // the chosen-avatar storage; ?type=picture is still honored as a dev/back-compat shortcut.
  const [avatarMode, setAvatarMode] = useState<"glb" | "picture">(() => {
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("type") === "picture"
    ) {
      return "picture";
    }
    return "glb";
  });
  const isPictureMode = avatarMode === "picture";

  // No GLTF loads in picture mode, so nothing flips glbReady — mark ready so the UI proceeds.
  useEffect(() => {
    if (isPictureMode) setGlbReady(true);
  }, [isPictureMode]);

  // WebRTC picture mode: connect once to the AVTR streamer's custom "external" engine and keep
  // the session alive (avatar always on screen + idling). Falls back to the MP4 path when off.
  const useAvtrWebRtc =
    isPictureMode && process.env.REACT_APP_USE_AVTR_WEBRTC === "true";
  // The persistent AVTR session always uses the WebSocket transport (fragmented-MP4 over MSE) —
  // NAT-proof, no STUN/TURN needed.
  const avtrClientRef = useRef<AvtrWsClient | null>(null);
  const avtrSpeakAbortRef = useRef<AbortController | null>(null);
  const avtrSpeakingRef = useRef(false); // true while the avatar's actual audio is playing
  const [avtrConnected, setAvtrConnected] = useState(false);
  const [pictureVideoReady, setPictureVideoReady] = useState(false);

  // The active photoreal character (from the current avatar id) — drives which renderer avatar is
  // streamed and the portrait/name shown in the connecting overlay. Undefined in GLB mode.
  const avtrRendererAvatarId = (avatar as any)?.avtrAvatarId ?? String((avatar as any)?.id ?? "");
  const avtrRendererBgId = (avatar as any)?.avtrBackgroundId ?? "plain_white";
  const avtrPortrait = (avatar as any)?.imagePath || (avatar as any)?.thumbnail || `${process.env.PUBLIC_URL || ""}/avatar-idle.jpg`;
  const avtrPortraitAsset = avtrPortrait;
  const avtrName = (avatar as any)?.name ?? "Avatar";
  const avtrNameAr = (avatar as any)?.name ?? "Avatar";

  // Voice for the photoreal characters: the backend already configures voices per (dialect, gender),
  // so we fetch the dialect's voices and pick the one matching the character's gender — the frontend
  // decides the voiceId and just sends it to /avatar-speak. Cached per dialect.
  const [avtrVoicesByDialect, setAvtrVoicesByDialect] = useState<Record<string, DialectVoiceDto[]>>({});
  useEffect(() => {
    const dialect = (avatar as any)?.dialectIsoCode || (avatar as any)?.languageCodes?.[0];
    if (!dialect || avtrVoicesByDialect[dialect] !== undefined) return;
    let cancelled = false;
    getVoicesForDialect(dialect)
      .then((list) => { if (!cancelled) setAvtrVoicesByDialect((p) => ({ ...p, [dialect]: list })); })
      .catch(() => { if (!cancelled) setAvtrVoicesByDialect((p) => ({ ...p, [dialect]: [] })); });
    return () => { cancelled = true; };
  }, [avatar, avtrVoicesByDialect]);

  // Resolve the ElevenLabs voiceId for a character: default voice for its gender, else any voice for
  // that gender, else the dialect's default. Null if voices haven't loaded (caller falls back).
  const resolveAvtrVoiceId = (): string | null => {
    const dialect = (avatar as any)?.dialectIsoCode || (avatar as any)?.languageCodes?.[0];
    if (!dialect) return null;
    const voices = avtrVoicesByDialect[dialect] ?? [];
    const byGender = voices.filter((v) => v.gender === (avatar as any)?.gender);
    const chosen =
      byGender.find((v) => v.isDefault) ?? byGender[0] ?? voices.find((v) => v.isDefault) ?? voices[0];
    return chosen?.voiceId ?? null;
  };

  // Assign the picture <video> ref and flag when it mounts (drives the connect effect).
  const setPictureVideoEl = useCallback((el: HTMLVideoElement | null) => {
    pictureVideoRef.current = el;
    setPictureVideoReady(!!el);
  }, []);

  // Open the persistent AVTR session when picture mode is on and reconnect if the streamer drops
  // the socket while the page is left idle.
  useEffect(() => {
    if (!useAvtrWebRtc || !settingsLoaded || !pictureVideoReady) return;
    if (avtrClientRef.current) return;
    const videoEl = pictureVideoRef.current;
    if (!videoEl) return;

    const baseUrl = process.env.REACT_APP_AVTR_STREAMER_URL || "";
    if (!baseUrl) {
      console.error("REACT_APP_AVTR_STREAMER_URL not set; cannot connect AVTR WebRTC");
      return;
    }

    videoEl.muted = true; // idle autoplay allowed; unmuted on first user gesture
    // Note: no setAvatarLoading here — the handshake shows its own "connecting" overlay
    // (avtrConnected drives it), distinct from the "thinking" state used for LLM responses.
    // Callbacks are identical across transports; only the client class + connect payload differ.
    let disposed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;
    let connectAvtr: () => void;

    const clearReconnectTimer = () => {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const scheduleReconnect = () => {
      if (disposed) return;
      clearReconnectTimer();
      const delay = Math.min(1000 * 2 ** reconnectAttempt, 10_000);
      reconnectAttempt += 1;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connectAvtr();
      }, delay);
    };

    const commonOpts = {
      baseUrl,
      videoEl,
      avatarId: avtrRendererAvatarId,
      backgroundId: avtrRendererBgId,
      onConnected: () => {
        reconnectAttempt = 0;
        setAvtrConnected(true);
        setGlbReady(true);
        setAvatarLoading(false);
      },
      onDisconnected: () => {
        if (disposed) return;
        setAvtrConnected(false);
        avtrClientRef.current = null;
        scheduleReconnect();
      },
      onError: (err: unknown) => {
        // Reconnect is NOT scheduled here: every failure path in the client ends in
        // disconnect(), which fires onDisconnected below. Scheduling here too would
        // double-count the backoff attempt. Do not clear avtrClientRef here either —
        // onDisconnected owns that, and clearing it in two places is how this wedged
        // before (ref left non-null => connectAvtr() short-circuits forever).
        console.error("AVTR ws connect failed", err);
        setAvtrConnected(false);
        setAvatarLoading(false);
      },
      // Drive the talking UI from her ACTUAL audio, not the request lifecycle.
      onSpeaking: (speaking: boolean) => {
        avtrSpeakingRef.current = speaking;
        if (speaking) {
          setAvatarTalking(true);
          setAvatarLoading(false);
        } else {
          setAvatarTalking(false);
          setReturnedText("");
          setIsProcessingResponse(false);
          handleUserStoppedSpeaking();
        }
      },
    };

    connectAvtr = () => {
      if (disposed || avtrClientRef.current) return;
      const client = new AvtrWsClient();
      avtrClientRef.current = client;
      void client.connect(commonOpts);
    };

    connectAvtr();

    return () => {
      disposed = true;
      clearReconnectTimer();
      avtrClientRef.current?.disconnect();
      avtrClientRef.current = null;
      setAvtrConnected(false);
    };
    // Reconnect when the selected character changes (avtrRendererAvatarId) so the streamer renders
    // the new avatar; the cleanup above tears down the old session first.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAvtrWebRtc, settingsLoaded, pictureVideoReady, avtrRendererAvatarId, avtrRendererBgId]);

  const hideMic = !settingsLoaded ? true : !tapToTalk;
  const autoStart = settingsLoaded ? !tapToTalk : false;
  const avtrInteractionDisabled = useAvtrWebRtc && !avtrConnected;

  const avatarTalkingRef = useRef(false);
  const isUserSpeakingRef = useRef(false);
  const isProcessingResponseRef = useRef(false);
  const hasStartedNextCyclePreloadRef = useRef(false);
  const hasRefetchedDuringPlaybackRef = useRef(false);

  const allVideos = useMemo(() => {
    return playlists.flatMap(
      (playlist) =>
        playlist.playlistVideos?.map((video: any) => ({
          ...video,
          playlistId: playlist.id,
        })) || []
    );
  }, [playlists]);

  const getAvatarFromList = useCallback(
    (avatarId: string | number, avatarsList: AvatarOption[]) => {
      return avatarsList.find(
        (item) => String(item.id) === String(avatarId)
      );
    },
    []
  );

  const resetSpeech = useCallback(() => {
    setAvatarAudio(null);

    morphTarget?.traverse((object: any) => {
      if (
        object.isMesh &&
        object.morphTargetDictionary &&
        object.morphTargetInfluences
      ) {
        const morphTargetDic = object.morphTargetDictionary;
        const morphTargetInfluences = object.morphTargetInfluences;

        Object.keys(morphTargetDic).forEach((key) => {
          const influenceIndex = morphTargetDic[key];

          if (influenceIndex !== undefined) {
            gsap.to(morphTargetInfluences, {
              [influenceIndex]: 0,
              duration: 0.5,
              ease: "sine.inOut",
            });
          }
        });
      }
    });
  }, [morphTarget]);

  const stopAudio = useCallback(() => {
    // WebRTC/Noura: the audio is rendered by the streamer, so stop it at the source (drops the
    // current segment + any buffered/incoming audio). GLB mode never touches the streamer —
    // its audio is a local <audio> element, stopped below.
    if (isPictureMode) {
      avtrSpeakAbortRef.current?.abort();
      avtrSpeakAbortRef.current = null;
      const { REACT_APP_AVATAR_SPEAK, REACT_APP_AVATAR_STOP }: any = process.env;
      const avatarStopUrl =
        REACT_APP_AVATAR_STOP ||
        (REACT_APP_AVATAR_SPEAK ? REACT_APP_AVATAR_SPEAK.replace(/avatar-speak$/, "avatar-stop") : "");
      if (avatarStopUrl) {
        fetch(avatarStopUrl, {
          method: "POST",
          headers: getAuthHeaders(),
        }).catch((error) => console.warn("Failed to stop AVTR speech via response service:", error));
      }
      void avtrClientRef.current?.interrupt();

      avtrSpeakingRef.current = false;
    }

    if (avatarAudio) {
      avatarAudio.pause();
    }

    setAvatarAudio(null);
    {
      const profile = SCHEME_PROFILES[morphScheme];
      handleAnimationTransition(avatarActions, profile.clips.idle, {
        alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
      });
    }
    setAvatarTalking(false);
    setAvatarLoading(false);
    setIsProcessingResponse(false);
    setReturnedText("");

    if (animationFrameId) {
      cancelAnimationFrame(animationFrameId);
      setAnimationFrameId(null);
    }

    resetSpeech();
  }, [avatarAudio, avatarActions, animationFrameId, resetSpeech, isPictureMode, morphScheme]);

  const applyAvatarSelection = useCallback(
    async (selectedAvatarData: any) => {
      if (!selectedAvatarData) return;

      try {
        const isSameAvatarModel =
          String(selectedAvatarData?.id ?? "") === String(avatar?.id ?? "") &&
          selectedAvatarData?.glbPath === avatar?.glbPath;

        stopAudio();
        setAvatarTalking(false);
        setAvatarLoading(false);
        setReturnedText("");

        if (!isSameAvatarModel) {
          setMorphTarget(null);
          setAvatarActions(null);
          setGlbReady(false);
        }

        setAvatar({ ...selectedAvatarData });

        if (selectedAvatarData?.themeId) {
          const avatarThemeResponse = await Axios.get(
            `${process.env.REACT_APP_BASE_URL}${endpoints.getAvatarTheme}/${selectedAvatarData.themeId}`
          );
          setAvatarTheme(avatarThemeResponse.data.data);
        }
      } catch (error) {
        console.error("Failed to apply avatar selection:", error);
      }
    },
    [avatar?.glbPath, avatar?.id, stopAudio]
  );

  useEffect(() => {
    const handleFirstClick = () => {
      const v = videoRef.current;
      if (!v) return;

      v.muted = false;
      v.play().catch(() => {
        v.muted = true;
        v.play()
          .then(() => {
            setTimeout(() => {
              try {
                v.muted = false;
              } catch { }
            }, 200);
          })
          .catch(() => { });
      });

      window.removeEventListener("click", handleFirstClick);
    };

    window.addEventListener("click", handleFirstClick);

    return () => {
      window.removeEventListener("click", handleFirstClick);
    };
  }, []);

  useEffect(() => {
    avatarTalkingRef.current = avatarTalking;
  }, [avatarTalking]);

  useEffect(() => {
    isUserSpeakingRef.current = isUserSpeaking;
  }, [isUserSpeaking]);

  useEffect(() => {
    isProcessingResponseRef.current = isProcessingResponse;
  }, [isProcessingResponse]);

  useEffect(() => {
    if (!avatarSettings) return;
    if (
      avatarSettings.isProactive &&
      settingsLoaded &&
      glbReady &&
      messages.length === 0 &&
      !proactiveStartedRef.current
    ) {
      setShowProactiveOverlay(true);
    }
    if (avatarSettings.isProactive === false) {
      setShowProactiveOverlay(false);
    }
  }, [
    avatarSettings,
    settingsLoaded,
    glbReady,
    messages.length,
  ]);

  useEffect(() => {
    if (avatarSettings) {
      const fallbackSpeakerLanguage =
        avatarSettings.speakerLanguageCode || "en-US";
      const fallbackAvatarLanguage =
        avatarSettings.speakerLanguageCode || "en-US";
      const fallbackSystemLanguage = normalizeSystemLanguage(
        avatarSettings.systemLanguage
      );

      const savedLanguages = localStorage.getItem("languages");
      const selectedAvatarId = getStoredChosenAvatarId();

      // Load user preference overrides (tap-to-talk, floating chat)
      const fallbackTapToTalk = !!avatarSettings.tapToTalk;
      const fallbackFloatingChat = !!avatarSettings.floatingChatEnabled;
      let userTapToTalk = fallbackTapToTalk;
      let userFloatingChat = fallbackFloatingChat;
      try {
        const savedPrefs = localStorage.getItem("user-preferences");
        if (savedPrefs) {
          const parsedPrefs = JSON.parse(savedPrefs);
          if (typeof parsedPrefs.tapToTalk === "boolean") userTapToTalk = parsedPrefs.tapToTalk;
          if (typeof parsedPrefs.floatingChatEnabled === "boolean") userFloatingChat = parsedPrefs.floatingChatEnabled;
        }
      } catch {}

      if (savedLanguages) {
        try {
          const parsed = JSON.parse(savedLanguages);
          const speakerLanguage = applySpeakerLanguagePolicy(
            parsed.speakerLanguage || fallbackSpeakerLanguage
          );
          const avatarLanguage =
            parsed.avatarLanguage || fallbackAvatarLanguage;
          const avatarVoiceId =
            typeof parsed.avatarVoiceId === "string" ? parsed.avatarVoiceId : "";
          const systemLanguage = normalizeSystemLanguage(
            parsed.systemLanguage || fallbackSystemLanguage
          );

          setPersonalizationForm({
            speakerLanguage,
            avatarLanguage,
            avatarVoiceId,
            selectedAvatarId,
            systemLanguage,
            tapToTalk: userTapToTalk,
            floatingChatEnabled: userFloatingChat,
          });

          applySystemLanguage(systemLanguage);
          setLanguage(
            resolveSpeakerLanguage(speakerLanguage, fallbackSpeakerLanguage)
          );
        } catch (error) {
          setPersonalizationForm({
            speakerLanguage: applySpeakerLanguagePolicy(fallbackSpeakerLanguage),
            avatarLanguage: fallbackAvatarLanguage,
            avatarVoiceId: "",
            selectedAvatarId,
            systemLanguage: fallbackSystemLanguage,
            tapToTalk: userTapToTalk,
            floatingChatEnabled: userFloatingChat,
          });

          applySystemLanguage(fallbackSystemLanguage);
          setLanguage(fallbackSpeakerLanguage);
        }
      } else {
        setPersonalizationForm({
          speakerLanguage: applySpeakerLanguagePolicy(fallbackSpeakerLanguage),
          avatarLanguage: fallbackAvatarLanguage,
          avatarVoiceId: "",
          selectedAvatarId,
          systemLanguage: fallbackSystemLanguage,
          tapToTalk: userTapToTalk,
          floatingChatEnabled: userFloatingChat,
        });

        applySystemLanguage(fallbackSystemLanguage);
        setLanguage(fallbackSpeakerLanguage);
      }
    }
  }, [avatarSettings]);

  useEffect(() => {
    const fetchHasPlaylist = async (): Promise<boolean> => {
      try {
        const response = await Axios.get(
          `${process.env.REACT_APP_BASE_URL}api/playlists/hasPlaylist`
        );
        return response.data.hasPlaylist;
      } catch (error) {
        return false;
      }
    };

    const checkPlaylistStatus = async () => {
      const result = await fetchHasPlaylist();
      setHasPlaylist(result);
    };

    checkPlaylistStatus();
  }, []);

  useEffect(() => {
    if (hasPlaylist === null) return;

    document.body.classList.toggle("has-playlist-layout", hasPlaylist);
    document.body.classList.toggle("original-layout", !hasPlaylist);

    return () => {
      document.body.classList.remove("has-playlist-layout", "original-layout");
    };
  }, [hasPlaylist]);

  const fetchActivePlaylistsOnce = async () => {
    try {
      const response = await Axios.get(
        `${process.env.REACT_APP_BASE_URL}api/playlists/active`
      );

      const playlistsWithVideos = response.data.filter(
        (playlist: any) =>
          playlist.playlistVideos && playlist.playlistVideos.length > 0
      );

      return playlistsWithVideos;
    } catch (error) {
      console.error("❌ Error fetching active playlists:", error);
      return [];
    }
  };

  useEffect(() => {
    if (hasPlaylist === null) return;

    const fetchAvatarAndSettings = async () => {
      try {
        // Detect device type and resolve channel-specific avatars
        const channelType = detectChannelType();
        let channelDefaultAvatar: any = null;
        let channelAvailableAvatars: any[] = [];
        let useChannelData = false;

        try {
          const channelResponse = await Axios.get(
            process.env.REACT_APP_BASE_URL + endpoints.resolveByChannel,
            { headers: { "X-Channel-Type": channelType } }
          );
          const channelData = channelResponse.data?.data;

          if (channelData && channelData.allowed === false) {
            setChannelBlocked(true);
            setLoading(false);
            return;
          }

          if (channelData?.defaultAvatar) {
            channelDefaultAvatar = channelData.defaultAvatar;
            channelAvailableAvatars = channelData.availableAvatars || [];
            useChannelData = true;
          }
        } catch (channelErr: any) {
          if (channelErr?.response?.status === 403) {
            setChannelBlocked(true);
            setLoading(false);
            return;
          }
          console.warn("Channel resolve failed, falling back to default:", channelErr);
        }

        const avatarSettingsResponse = await Axios.get(
          process.env.REACT_APP_BASE_URL + endpoints.getAvatarSettings
        );

        const playlistSettingsResponse = await Axios.get(
          process.env.REACT_APP_BASE_URL + endpoints.getPlaylistSettings
        );

        // If channel resolved, use its avatar list; otherwise fall back to all avatars + global default
        let backendDefaultAvatar = channelDefaultAvatar;
        let avatarsList = channelAvailableAvatars;

        if (!useChannelData) {
          const avatarResponse = await Axios.get(
            process.env.REACT_APP_BASE_URL + endpoints.getAvatarDefault
          );
          backendDefaultAvatar = avatarResponse.data.data;

          const avatarsResponse = await Axios.get(
            `${process.env.REACT_APP_BASE_URL}avatar`
          );
          avatarsList = avatarsResponse.data?.data || [];
        }

        const storedAvatarId = getStoredChosenAvatarId();

        // Validate stored choice against available avatars
        const storedAvatarInList = storedAvatarId
          ? avatarsList.find((a: any) => String(a.id) === storedAvatarId)
          : null;

        const resolvedAvatar = storedAvatarInList || backendDefaultAvatar;

        let resolvedTheme = null;

        if (resolvedAvatar?.themeId) {
          try {
            const avatarThemeResponse = await Axios.get(
              `${process.env.REACT_APP_BASE_URL}${endpoints.getAvatarTheme}/${resolvedAvatar.themeId}`
            );
            resolvedTheme = avatarThemeResponse.data.data;
          } catch (themeError) {
            console.error("Failed to fetch resolved avatar theme:", themeError);
          }
        }

        // Mint a session only when there is no conversation to continue. This runs several
        // awaited requests into page load, so resetting unconditionally here would silently
        // replace the session id an already-open conversation is using — the next turn would then
        // be filed under an orphan id and spawn a brand-new conversation.
        if (!routeSessionIdRef.current && !sessionMintedRef.current) {
          sessionMintedRef.current = true;
          initNewSessionId();
        }

        setAvatarOptions(avatarsList);
        setLanguage(avatarSettingsResponse.data.data.speakerLanguageCode);
        setAvatarSettings(avatarSettingsResponse.data.data);
        setAvatar({ ...resolvedAvatar });
        setAvatarMode(isImageAvatar(resolvedAvatar) ? "picture" : "glb");

        if (resolvedTheme) {
          setAvatarTheme(resolvedTheme);
        }

        setLoading(false);
        setSettingsLoaded(true);

        const selectedAvatarId = resolvedAvatar?.id ? String(resolvedAvatar.id) : "";

        setPersonalizationForm((prev) => ({
          ...prev,
          selectedAvatarId,
        }));

        const playlistData = playlistSettingsResponse.data?.data;
        const idleSeconds = Number(playlistData?.idleTime);

        if (Number.isFinite(idleSeconds) && idleSeconds > 0) {
          setIdleTimeoutDuration(idleSeconds * 1000);
        } else {
          setIdleTimeoutDuration(10000);
        }
      } catch (error) {
        console.error("Failed to fetch avatar/settings:", error);
        setLoading(false);
      }
    };

    const initPlaylistsPipeline = async () => {
      if (!hasPlaylist) {
        setPlaylists([]);
        setNextPlaylists([]);
        setCurrentVideoIndex(0);
        setEverythingLoaded(true);
        return;
      }

      hasStartedNextCyclePreloadRef.current = false;

      const firstBatch = await fetchActivePlaylistsOnce();

      const firstBatchVideos = firstBatch.flatMap((playlist: any) =>
        (playlist.playlistVideos || []).map((v: any) => ({
          url: v.url,
          playlistId: playlist.id,
        }))
      );

      if (firstBatchVideos.length === 0) {
        console.warn("⚠️ Set 1 has no videos, marking everythingLoaded");
        setPlaylists(firstBatch);
        setCurrentVideoIndex(0);
        setEverythingLoaded(true);
        return;
      }

      const [firstVideo, ...restVideos] = firstBatchVideos;

      await preloadVideoNoTimeout(firstVideo.url);

      if (restVideos.length > 0) {
        Promise.all(
          restVideos.map((v: { url: string }) => preloadVideo(v.url))
        ).then(() => { });
      }

      setPlaylists(firstBatch);
      setCurrentVideoIndex(0);
      setEverythingLoaded(true);
    };

    fetchAvatarAndSettings();
    setTimeout(() => {
      initPlaylistsPipeline();
    }, 0);
  }, [hasPlaylist, getAvatarFromList]);

  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (
      !everythingLoaded ||
      !hasPlaylist ||
      !videoRef.current ||
      allVideos.length === 0
    ) {
      return;
    }

    const video = videoRef.current;

    const handleVideoEnded = async () => {
      hasRefetchedDuringPlaybackRef.current = false;

      const isLastVideo = currentVideoIndex >= allVideos.length - 1;

      if (nextPlaylists.length > 0 && isLastVideo) {
        setPlaylists(nextPlaylists);
        setNextPlaylists([]);
        setCurrentVideoIndex(0);
        hasStartedNextCyclePreloadRef.current = false;
        return;
      }

      if (!isLastVideo) {
        if (currentVideoIndex === 0 && !hasStartedNextCyclePreloadRef.current) {
          hasStartedNextCyclePreloadRef.current = true;

          const nextBatch = await fetchActivePlaylistsOnce();
          setNextPlaylists(nextBatch);

          const urls = nextBatch.flatMap((playlist: any) =>
            (playlist.playlistVideos || []).map((v: any) => v.url)
          );

          Promise.all(urls.map(preloadVideo));
        }

        setCurrentVideoIndex((prev) => prev + 1);
        return;
      }

      const batchToUse =
        nextPlaylists.length > 0
          ? nextPlaylists
          : await fetchActivePlaylistsOnce();

      setPlaylists(batchToUse);
      setNextPlaylists([]);
      setCurrentVideoIndex(0);

      hasStartedNextCyclePreloadRef.current = false;

      const futureBatch = await fetchActivePlaylistsOnce();
      setNextPlaylists(futureBatch);

      const futureUrls = futureBatch.flatMap((playlist: any) =>
        (playlist.playlistVideos || []).map((v: any) => v.url)
      );

      Promise.all(futureUrls.map(preloadVideo));
    };

    video.addEventListener("ended", handleVideoEnded);

    const playPromise = video.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise
        .then(() => {
          video.muted = false;
          setVideoStatus("playing");
        })
        .catch(() => {
          video.muted = true;
          return video.play();
        })
        .then(() => {
          if (video.muted) {
            video.muted = false;
            setVideoStatus("playing");
          }
        })
        .catch(() => { });
    }

    return () => {
      video.removeEventListener("ended", handleVideoEnded);
    };
  }, [
    everythingLoaded,
    hasPlaylist,
    allVideos,
    currentVideoIndex,
    nextPlaylists,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      hasRefetchedDuringPlaybackRef.current = false;
    };

    video.addEventListener("play", handlePlay);
    return () => video.removeEventListener("play", handlePlay);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hasPlaylist) return;

    const handleTimeUpdate = async () => {
      if (!video.duration || isNaN(video.duration)) return;

      const progress = video.currentTime / video.duration;

      if (progress > 0.8 && !hasRefetchedDuringPlaybackRef.current) {
        hasRefetchedDuringPlaybackRef.current = true;

        console.log("🔄 Refetching /active before video ends");

        const freshBatch = await fetchActivePlaylistsOnce();
        setNextPlaylists(freshBatch);

        const urls = freshBatch.flatMap((playlist: any) =>
          (playlist.playlistVideos || []).map((v: any) => v.url)
        );

        Promise.all(urls.map(preloadVideo));
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [hasPlaylist]);

  useEffect(() => {
    if (!everythingLoaded || !hasPlaylist) return;
    if (!videoRef.current || allVideos.length === 0) return;
    if (currentVideoIndex >= allVideos.length) return;

    const v = videoRef.current;
    let cancelled = false;

    const next = allVideos[currentVideoIndex];
    const nextSrc = next?.url ?? "";

    v.pause();
    v.removeAttribute("src");
    v.load();

    const onCanPlay = async () => {
      if (cancelled) return;

      // Don't autoplay if the user is talking, the avatar is responding, or a response
      // is being processed — those states are paused intentionally and tryResume()
      // will start playback again once they all clear.
      if (
        avatarTalkingRef.current ||
        isUserSpeakingRef.current ||
        isProcessingResponseRef.current
      ) {
        return;
      }

      try {
        if (document.visibilityState === "visible") {
          await v.play();
          v.muted = false;
        } else {
          v.muted = true;
          await v.play();
        }
      } catch {
        try {
          v.muted = true;
          await v.play();
          setTimeout(() => {
            if (!cancelled) v.muted = false;
          }, 100);
        } catch { }
      }
    };

    v.addEventListener("canplay", onCanPlay, { once: true });
    v.preload = "auto";
    if (nextSrc) {
      fetchAsBlobUrl(nextSrc)
        .then((blobUrl) => {
          if (cancelled) return;
          v.src = blobUrl;
          v.load();
        })
        .catch((err) => {
          if (cancelled) return;
          console.error("Failed to fetch playlist video:", nextSrc, err);
          v.src = nextSrc;
          v.load();
        });
    } else {
      v.load();
    }

    return () => {
      cancelled = true;
      v.removeEventListener("canplay", onCanPlay);
    };
  }, [currentVideoIndex, everythingLoaded, hasPlaylist, allVideos]);

  const handleUserStartedSpeaking = () => {
    setIsUserSpeaking(true);

    if (useAvtrWebRtc) {
      // First gesture: unmute the avatar audio, and feed the user's voice for active-listening.
      avtrClientRef.current?.unmute();
      avtrClientRef.current?.startUserAudio();
    }

    if (!tapToTalk && autoStart) {
      setIsAvatarListeningIndicator(true);
    }

    if (videoRef.current && hasPlaylist && allVideos.length > 0) {
      if (!videoRef.current.paused) {
        videoRef.current.pause();
        setVideoStatus("paused");

        if (isFloatingChatEnabled) {
          setShowChatBox(true);
        }

        if (videoStatusTimeoutRef.current) {
          clearTimeout(videoStatusTimeoutRef.current);
        }

        videoStatusTimeoutRef.current = setTimeout(() => {
          setVideoStatus("playing");
        }, 2000);
      }
    }

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  };

  const handleUserStoppedSpeaking = async () => {
    setIsUserSpeaking(false);
    setIsAvatarListeningIndicator(false);

    if (useAvtrWebRtc) {
      avtrClientRef.current?.stopUserAudio();
    }

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    inactivityTimerRef.current = setTimeout(() => {
      const tryResume = () => {
        const video = videoRef.current;

        if (!video || !hasPlaylist) return;

        if (!video.paused) {
          if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
          }
          return;
        }

        if (
          !avatarTalkingRef.current &&
          !isUserSpeakingRef.current &&
          !isProcessingResponseRef.current
        ) {
          const playPromise = video.play();

          if (playPromise && typeof playPromise.then === "function") {
            playPromise
              .then(() => {
                video.muted = false;
                setVideoStatus("resumed");
                setSessionResetCounter((c) => c + 1);

                if (isFloatingChatEnabled) {
                  setShowChatBox(false);
                }

                if (videoStatusTimeoutRef.current) {
                  clearTimeout(videoStatusTimeoutRef.current);
                }

                videoStatusTimeoutRef.current = setTimeout(() => {
                  setVideoStatus("playing");
                }, 2000);

                if (inactivityTimerRef.current) {
                  clearTimeout(inactivityTimerRef.current);
                  inactivityTimerRef.current = null;
                }
              })
              .catch(() => {
                if (inactivityTimerRef.current) {
                  clearTimeout(inactivityTimerRef.current);
                }

                inactivityTimerRef.current = setTimeout(tryResume, 1000);
              });
          }
        } else {
          if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
          }

          inactivityTimerRef.current = setTimeout(tryResume, 1000);
        }
      };

      tryResume();
    }, idleTimeoutDuration);
  };

  // Universal video resume: whenever the avatar stops talking, the user stops speaking,
  // and no response is processing, resume the playlist. Covers stopAudio() interrupts,
  // error paths, and any other state transition that clears all three flags — not just
  // the user-mic-stop path that handleUserStoppedSpeaking already handles.
  useEffect(() => {
    if (!hasPlaylist || !everythingLoaded) return;
    if (avatarTalking || isUserSpeaking || isProcessingResponse) return;

    const v = videoRef.current;
    if (!v || !v.paused || allVideos.length === 0) return;

    const playPromise = v.play();
    if (playPromise && typeof playPromise.then === "function") {
      playPromise.catch(() => {
        v.muted = true;
        v.play()
          .then(() => {
            setTimeout(() => {
              try { v.muted = false; } catch { }
            }, 200);
          })
          .catch(() => { });
      });
    }
  }, [hasPlaylist, everythingLoaded, avatarTalking, isUserSpeaking, isProcessingResponse, allVideos.length]);

  useEffect(() => {
    return () => {
      if (videoStatusTimeoutRef.current) {
        clearTimeout(videoStatusTimeoutRef.current);
      }
    };
  }, []);

  const handleSavePersonalization = async (draft: typeof personalizationForm) => {
    try {
      const normalizedSystemLang = normalizeSystemLanguage(draft.systemLanguage);
      const previousSystemLang = normalizeSystemLanguage(
        personalizationForm.systemLanguage,
      );
      const systemLangChanged = normalizedSystemLang !== previousSystemLang;

      const speakerLanguage = applySpeakerLanguagePolicy(draft.speakerLanguage);

      const languages = {
        speakerLanguage,
        avatarLanguage: draft.avatarLanguage,
        avatarVoiceId: draft.avatarVoiceId,
        systemLanguage: normalizedSystemLang,
      };

      const fallbackSpeakerLanguage =
        avatarSettings?.speakerLanguageCode || "en-US";

      const selectedAvatarOption = avatarOptions.find(
        (item) => String(item.id) === String(draft.selectedAvatarId)
      );

      localStorage.setItem("languages", JSON.stringify(languages));

      localStorage.setItem(
        "user-preferences",
        JSON.stringify({
          tapToTalk: draft.tapToTalk,
          floatingChatEnabled: draft.floatingChatEnabled,
        })
      );

      if (selectedAvatarOption) {
        localStorage.setItem(
          CHOSEN_AVATAR_STORAGE_KEY,
          JSON.stringify({
            id: selectedAvatarOption.id,
            name: selectedAvatarOption.name,
            elevenLabsVoiceId: selectedAvatarOption.elevenLabsVoiceId,
          })
        );
      } else {
        // User didn't explicitly pick an avatar in the form — clear the stored choice but
        // leave the currently displayed avatar untouched. Falling back to avatarOptions[0]
        // here would visibly swap the avatar (e.g. after logout, when channel-resolve had
        // surfaced a non-global-default avatar on screen).
        localStorage.removeItem(CHOSEN_AVATAR_STORAGE_KEY);
      }

      // System language change requires a full reload so every i18n/RTL-dependent
      // mount re-evaluates against the new locale. Persist first, reload last —
      // everything else (avatar, voice, dialect, speaker) is naturally re-read from
      // localStorage on init, so the rest of this handler can be skipped.
      if (systemLangChanged) {
        window.location.reload();
        return;
      }

      // A synthetic AVTR option switches to that photoreal live (WebRTC) character — there's no GLB
      // to load. Any real avatar switches back to GLB mode and (re)loads its model.
      if (selectedAvatarOption && isImageAvatar(selectedAvatarOption)) {
        setAvatarMode("picture");
        // Make the app identity that character (name shows correctly in chat, typing indicator,
        // etc.); the connect effect reconnects the stream to its renderer avatar. Voice still
        // comes from the picked voice / override.
        if (String(selectedAvatarOption.id) !== String((avatar as any)?.id ?? "")) {
          await applyAvatarSelection(selectedAvatarOption);
        }
      } else {
        setAvatarMode("glb");
        // Only re-apply when the user picked a *different* avatar than the one already on
        // screen. Dialect / voice / language changes never call applyAvatarSelection.
        const currentAvatarId = String((avatar as any)?.id ?? "");
        if (
          selectedAvatarOption &&
          String(selectedAvatarOption.id) !== currentAvatarId
        ) {
          await applyAvatarSelection(selectedAvatarOption);
        }
      }

      setPersonalizationForm({
        ...draft,
        speakerLanguage,
        systemLanguage: normalizedSystemLang,
        selectedAvatarId: selectedAvatarOption
          ? String(selectedAvatarOption.id)
          : "",
      });

      setLanguage(
        resolveSpeakerLanguage(speakerLanguage, fallbackSpeakerLanguage)
      );

      setShowSettingsModal(false);
    } catch (error) {
      console.error("Failed to save personalization:", error);
    }
  };

  const renderAvatar = useMemo(() => {
    if (!loading && avatar?.glbPath) {
      // Use per-avatar camera settings, falling back to global avatarSettings
      const effectiveSettings = {
        ...avatarSettings,
        positionSettings: avatar?.positionSettings ?? avatarSettings?.positionSettings,
        cameraFov: avatar?.cameraFov ?? avatarSettings?.cameraFov,
        cameraSettings: avatar?.cameraSettings ?? avatarSettings?.cameraSettings,
      };
      const profile = SCHEME_PROFILES[morphScheme];
      return (
        <>
          {profile.enableExtraLights && (
            <>
              <ambientLight intensity={2} />
              <directionalLight
                position={[5, 5, 5]}
                intensity={2}
                castShadow
                shadow-mapSize={1024}
              />
            </>
          )}
          <GLTFModelLoader
            key={`${avatar?.id}-${avatar?.glbPath}`}
            avatarSettings={effectiveSettings}
            isBackground={false}
            actionsCallback={(actions) => { setAvatarActions(actions); setGlbReady(true); }}
            url={avatar.glbPath}
            setMorphTarget={(m) => setMorphTarget(m)}
            onSchemeDetected={setMorphScheme}
          />
        </>
      );
    }

    return null;
  }, [
    loading,
    avatar?.id,
    avatar?.glbPath,
    avatar?.cameraFov,
    avatar?.cameraSettings,
    avatar?.positionSettings,
    avatarSettings,
    morphScheme,
  ]);

  // Picture-mode avatar surface.
  //  - WebRTC mode: a single <video> bound to the persistent AVTR stream. It's ALWAYS live —
  //    the streamer sends continuous idle motion and switches to speech when we push audio.
  //  - MP4 fallback: two stacked layers (a still + CSS "breathing" behind, the streamed talking
  //    clip crossfaded on top) so the avatar is always visible without a black frame.
  const pictureAvatarEl = useAvtrWebRtc ? (
    <div
      className="picture-avatar-container"
      style={{ position: "relative", width: "100%", height: "100%", background: "#000", overflow: "hidden" }}
    >
      <style>{`
        @keyframes avtrConnRing {
          0%   { transform: scale(0.35); opacity: 0.65; }
          100% { transform: scale(2.3); opacity: 0; }
        }
        @keyframes avtrConnBreath {
          from { transform: scale(1.03); }
          to   { transform: scale(1.09); }
        }
        @keyframes avtrConnCore {
          0%, 100% { box-shadow: 0 0 18px 3px rgba(51,51,255,0.55); }
          50%      { box-shadow: 0 0 30px 7px rgba(51,51,255,0.85); }
        }
        @keyframes avtrConnDot { 0%,20%{opacity:.2} 50%{opacity:1} 80%,100%{opacity:.2} }
        @keyframes avtrConnFade { from { opacity: 0 } to { opacity: 1 } }
        @media (prefers-reduced-motion: reduce) {
          .avtr-conn-ring, .avtr-conn-bg, .avtr-conn-core, .avtr-conn-dot { animation: none !important; }
          .avtr-conn-ring { opacity: 0.25 !important; }
        }
      `}</style>
      <video
        ref={setPictureVideoEl}
        className="picture-avatar-video"
        autoPlay
        playsInline
        muted
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
      />
      {!avtrConnected && (
        <div
          className="avtr-conn"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#000",
            animation: "avtrConnFade 0.4s ease",
          }}
        >
          {/* Her own portrait, dimmed + softly breathing — she's coming online. */}
          <img
            className="avtr-conn-bg"
            src={avtrPortraitAsset}
            alt=""
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "contain",
              opacity: 0.26,
              filter: "blur(3px) brightness(0.7)",
              animation: "avtrConnBreath 6s ease-in-out infinite alternate",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at center, rgba(0,0,0,0.15), rgba(0,0,0,0.78))",
            }}
          />
          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "26px",
            }}
          >
            <div style={{ position: "relative", width: "96px", height: "96px", display: "grid", placeItems: "center" }}>
              {[0, 0.8, 1.6].map((delay) => (
                <span
                  key={delay}
                  className="avtr-conn-ring"
                  style={{
                    position: "absolute",
                    width: "96px",
                    height: "96px",
                    borderRadius: "50%",
                    border: "1.5px solid rgba(51,51,255,0.55)",
                    animation: "avtrConnRing 2.4s ease-out infinite",
                    animationDelay: `${delay}s`,
                  }}
                />
              ))}
              <span
                className="avtr-conn-core"
                style={{
                  width: "13px",
                  height: "13px",
                  borderRadius: "50%",
                  background: "#3333ff",
                  animation: "avtrConnCore 2s ease-in-out infinite",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "3px",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 500,
                letterSpacing: "0.04em",
              }}
            >
              <span>
                {systemLanguage === "ar" ? `جارٍ الاتصال بـ${avtrNameAr}` : `Connecting to ${avtrName}`}
              </span>
              {[0, 0.2, 0.4].map((delay) => (
                <span
                  key={delay}
                  className="avtr-conn-dot"
                  style={{ animation: "avtrConnDot 1.4s ease-in-out infinite", animationDelay: `${delay}s` }}
                >
                  .
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  ) : (
    <div
      className="picture-avatar-container"
      style={{ position: "relative", width: "100%", height: "100%", background: "#000", overflow: "hidden" }}
    >
      <style>{`
        @keyframes avatarBreathe {
          from { transform: scale(1) translateY(0); }
          to   { transform: scale(1.012) translateY(-2px); }
        }
      `}</style>
      <img
        className="picture-avatar-idle"
        src={avtrPortraitAsset}
        alt=""
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          animation: "avatarBreathe 6s ease-in-out infinite alternate",
        }}
      />
      <video
        ref={setPictureVideoEl}
        className="picture-avatar-video"
        playsInline
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          opacity: avatarTalking ? 1 : 0,
          transition: "opacity 0.35s ease",
        }}
      />
    </div>
  );

  const animateSpeech = (audioElement: HTMLAudioElement) => {
    if (!morphTarget) return;

    const schemeAtStart = morphScheme;
    const phonemeMap = getPhonemeMap(schemeAtStart);
    const morphDuration = SCHEME_PROFILES[schemeAtStart].morphDuration;

    const phonemeQueue: Array<{
      phoneme: string;
      start: number;
      end: number;
    }> = [];
    let currentAlignmentIndex = 0;
    let currentAlignmentRevision = alignmentsRevisionRef.current;

    const processNewAlignments = (alignments: any[]) => {
      if (currentAlignmentRevision !== alignmentsRevisionRef.current) {
        phonemeQueue.length = 0;
        currentAlignmentIndex = 0;
        currentAlignmentRevision = alignmentsRevisionRef.current;
      }

      for (let i = currentAlignmentIndex; i < alignments.length; i++) {
        const alignment = alignments[i];
        const characters = alignment.characters;
        const startTimes = alignment.character_start_times_seconds;
        const endTimes = alignment.character_end_times_seconds;

        if (!characters || !startTimes || !endTimes) continue;

        characters.forEach((char: string, index: number) => {
          phonemeQueue.push({
            phoneme: isArabic(char)
              ? extractArabicPhonemes(char)[0]
              : extractEnglishPhonemes(char)[0],
            start: startTimes[index],
            end: endTimes[index],
          });
        });
      }

      currentAlignmentIndex = alignments.length;
    };

    const morphTargetData: any[] = [];

    morphTarget.traverse((object: any) => {
      if (object.isMesh && object.morphTargetDictionary) {
        morphTargetData.push({
          morphTargetDic: object.morphTargetDictionary,
          morphTargetInfluences: object.morphTargetInfluences,
        });
      }
    });

    let frameId: number;
    let lastPhoneme: string | null = null;

    const animate = () => {
      if (audioElement.paused || audioElement.ended) {
        cancelAnimationFrame(frameId);
        resetSpeech();
        return;
      }

      processNewAlignments(alignmentsRef.current);

      const currentTime = audioElement.currentTime;
      let currentPhoneme = "default";

      for (const { phoneme, start, end } of phonemeQueue) {
        if (currentTime >= start && currentTime <= end) {
          currentPhoneme = phoneme;
          break;
        }
      }

      if (currentPhoneme !== lastPhoneme) {
        const currentTargets =
          phonemeMap[currentPhoneme] || phonemeMap.default;

        morphTargetData.forEach(({ morphTargetDic, morphTargetInfluences }) => {
          Object.keys(morphTargetDic).forEach((key) => {
            const index = morphTargetDic[key];

            if (index !== undefined) {
              gsap.to(morphTargetInfluences, {
                [index]: currentTargets[key] || 0,
                duration: morphDuration,
                ease: "power2.out",
              });
            }
          });
        });

        lastPhoneme = currentPhoneme;
      }

      frameId = requestAnimationFrame(animate);
    };

    frameId = requestAnimationFrame(animate);
    setAnimationFrameId(frameId);
  };

  // Conversation history functions
  const loadConversations = useCallback(async (page = 0, showLoading = true) => {
    if (!userAuthenticated || !userId) return;
    try {
      if (showLoading) {
        if (page === 0) setLoadingConversations(true);
        else setIsLoadingMoreConversations(true);
      }

      const result = await fetchConversations(userId, page, 20);
      const backendConvs = result.content || [];

      if (page === 0) {
        // Merge: keep optimistic entries that backend doesn't know about yet
        setConversations((prev) => {
          const backendIds = new Set(backendConvs.map((c: ConversationSummary) => c.sessionId));
          const optimisticOnly = prev.filter((c) => !backendIds.has(c.sessionId));
          return [...optimisticOnly, ...backendConvs];
        });
      } else {
        setConversations((prev) => {
          const existingIds = new Set(prev.map((c) => c.sessionId));
          const unique = backendConvs.filter((c: ConversationSummary) => !existingIds.has(c.sessionId));
          return [...prev, ...unique];
        });
      }

      setHasMoreConversations(!result.last);
      setConversationPage(page);
    } catch (e) {
      console.error("Failed to load conversations", e);
    } finally {
      setLoadingConversations(false);
      setIsLoadingMoreConversations(false);
    }
  }, [userAuthenticated, userId]);

  useEffect(() => {
    if (userAuthenticated && userId) {
      loadConversations(0);
    }
  }, [userAuthenticated, userId, loadConversations]);

  // Load shared conversation messages in read-only mode
  useEffect(() => {
    if (isReadOnly && shareToken) {
      const loadShared = async () => {
        try {
          const msgs = await fetchSharedMessages(shareToken);
          const formatted = msgs.flatMap((m) => [
            { sender: "client", text: m.inputText },
            { sender: "ai", text: m.responseText },
          ]);
          setMessages(formatted);
          setIsChatSidebarOpen(true);
          setShowChatBox(true);
        } catch (e) {
          console.error("Failed to load shared conversation", e);
        }
      };
      loadShared();
    }
  }, [isReadOnly, shareToken]);

  const handleSelectConversation = useCallback(async (sessionId: string) => {
    // An explicit open supersedes any navigation this page was waiting on.
    pendingNavigationSessionIdRef.current = null;
    if (clearMessagesTimeoutRef.current) {
      clearTimeout(clearMessagesTimeoutRef.current);
      clearMessagesTimeoutRef.current = null;
    }

    // Make the opened conversation the active session BEFORE awaiting the message
    // fetch, so a fast follow-up can't race the in-flight load and get filed under
    // the previous (stale) sessionId. getAiResponse reads getSessionId() for both
    // the request payload and createOrUpdateSession, so this keeps the follow-up in
    // this same conversation instead of spawning a new one.
    setSessionId(sessionId);

    // Stop avatar if it's currently talking
    if (avatarTalking || isProcessingResponse) {
      stopAudio();
      setIsProcessingResponse(false);
    }

    try {
      const msgs = await fetchConversationMessages(sessionId);
      // Dedupe by conversationId: if the same turn was persisted more than once (e.g. both the
      // frontend and the response-service wrote it), collapse to a single entry.
      const seenConvIds = new Set<string>();
      const dedupedMsgs = msgs.filter((m) => {
        const id = m.conversationId;
        if (!id) return true;
        if (seenConvIds.has(id)) return false;
        seenConvIds.add(id);
        return true;
      });
      const formatted = dedupedMsgs.flatMap((m) => [
        { sender: "client", text: m.inputText },
        { sender: "ai", text: m.responseText, conversationId: m.conversationId },
      ]);
      setMessages(formatted);
      setCurrentConversationSessionId(sessionId);
      if (shouldDockSidebarBottom) {
        setIsSidebarOpen(false);
      }
    } catch (e) {
      console.error("Failed to load conversation messages", e);
    }
  }, [avatarTalking, isProcessingResponse, shouldDockSidebarBottom, stopAudio]);

  // Load conversation from URL parameter (like cb's /chat/:conversationId)
  useEffect(() => {
    if (!urlSessionId) {
      pendingNavigationSessionIdRef.current = null;
      return;
    }

    const pendingNavigation = pendingNavigationSessionIdRef.current;
    if (pendingNavigation) {
      // A navigation this page issued itself, after a turn created/updated its conversation. The
      // URL and currentConversationSessionId don't commit in the same render, so ignore every run
      // until the route catches up (otherwise we'd load the conversation the URL still names),
      // then consume the marker without fetching: the conversation is already in state, and
      // re-fetching it here would race the turn's own message append and render the answer twice.
      if (urlSessionId === pendingNavigation) {
        pendingNavigationSessionIdRef.current = null;
      }
      return;
    }

    // The route is the source of truth for which conversation a turn belongs to, so re-assert it
    // on the stored session id as well. If anything replaced that id while this conversation was
    // open, the next turn would be filed under a foreign session — and nothing else would notice,
    // because currentConversationSessionId still matches the URL.
    if (getSessionId() !== urlSessionId) {
      setSessionId(urlSessionId);
    }

    if (urlSessionId !== currentConversationSessionId) {
      handleSelectConversation(urlSessionId);
    }
  }, [urlSessionId, currentConversationSessionId, handleSelectConversation]);

  const handleNewConversation = () => {
    if (clearMessagesTimeoutRef.current) {
      clearTimeout(clearMessagesTimeoutRef.current);
      clearMessagesTimeoutRef.current = null;
    }
    setMessages([]);
    initNewSessionId();
    setCurrentConversationSessionId(null);
    navigate('/');
    setSessionResetCounter((c: number) => c + 1);
  };

  const handleConversationDeleted = (sessionId: string) => {
    setConversations((prev) => prev.filter((c) => c.sessionId !== sessionId));
    if (currentConversationSessionId === sessionId) {
      handleNewConversation();
    }
  };

  const handleConversationRenamed = (sessionId: string, title: string) => {
    setConversations((prev) =>
      prev.map((c) => (c.sessionId === sessionId ? { ...c, title } : c))
    );
  };

  const getAiResponse = async (
    transcript: string,
    detectedLanguage?: string,
    options?: { isNewChat?: boolean }
  ) => {
    const isNewChat = options?.isNewChat === true;
    if (avatarTalking) return;
    if (!isNewChat && !transcript) return;

    const { REACT_APP_AVATAR_RESPONSE }: any = process.env;

    // Pin the session this turn belongs to. If the user switches conversations while the response
    // is in flight, getSessionId() changes — but this turn must stay filed under (and shown in)
    // the conversation it started in, never bleed into the newly-opened one.
    const turnSessionId = getSessionId();

    setIsProcessingResponse(true);
    setAvatarLoading(true);
    {
      const profile = SCHEME_PROFILES[morphScheme];
      handleAnimationTransition(avatarActions, profile.clips.thinking, {
        alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
      });
    }
    resetSpeech();

    if (!isNewChat) {
      setMessages((prevMessages: any) => [
        ...prevMessages,
        { sender: "client", text: transcript },
      ]);
    }

    try {
      const fallbackSpeakerLanguage =
        avatarSettings?.speakerLanguageCode || language || "en-US";
      let selectedSpeakerLanguage = fallbackSpeakerLanguage;

      try {
        const savedLanguages = localStorage.getItem("languages");
        if (savedLanguages) {
          const parsed = JSON.parse(savedLanguages);
          selectedSpeakerLanguage = applySpeakerLanguagePolicy(
            parsed.speakerLanguage || fallbackSpeakerLanguage
          );
        }
      } catch (error) {
        console.error("Failed to parse languages from localStorage:", error);
      }

      const backendLangCode = resolveSpeakerLanguage(
        selectedSpeakerLanguage,
        fallbackSpeakerLanguage
      );

      let formattedMessages: { role: string; content: string }[];
      if (isNewChat) {
        formattedMessages = [];
      } else {
        formattedMessages = messages.map((msg: any) => ({
          role: msg.sender === "client" ? "user" : "assistant",
          content: msg.text,
        }));
        formattedMessages.push({
          role: "user",
          content: transcript,
        });
      }

      // The avatar language chosen in settings ("ar-LB"), which carries the dialect. Read once so
      // the language and the dialect on the request can never come from different selections.
      const avatarLanguageIso =
        personalizationForm.avatarLanguage || backendLangCode;

      const payload = {
        sessionId: turnSessionId,
        messages: formattedMessages,
        is_new_chat: isNewChat,
        // LLM expects a base language code; strip any dialect/region subtag.
        language: extractBaseLanguage(avatarLanguageIso),
        // The dialect the same selection names on the chat API ("ar-LB" -> "Lebanese"), or null
        // when it carries none the service knows — it answers 422 for an unknown dialect.
        dialect: resolveChatDialect(avatarLanguageIso),
        userId: userId || null,
        // Display name for the admin-side noise log, so a detection names the
        // user instead of showing only the opaque identity. Null for guests.
        userName: getStoredUserName(),
      };

      const startTime = Date.now();

      // Real-time streaming path (AVTR-1 WebRTC picture mode): stream the LLM answer through
      // ElevenLabs into the live session as it's generated, so the avatar starts talking WHILE the
      // LLM is still producing text — instead of waiting for the full answer then synthesizing it.
      // Returns true when it owns the turn (display + audio + history). Falls through to the legacy
      // buffered path below only if streaming is unavailable or fails before any token arrives.
      // GLB/MP4 modes always use the legacy path.
      if (useAvtrWebRtc) {
        const handled = await tryStreamTurn(payload, {
          transcript,
          turnSessionId,
          backendLangCode,
          startTime,
          isNewChat,
        });
        if (handled) return;
        console.warn("[avatar-speak-stream] unavailable; using non-streaming fallback");
      }

      const response = await Axios.post(REACT_APP_AVATAR_RESPONSE, payload);
      console.log("✅ Backend response:", response.data);
      const data: any = response.data;

      if (data.data?.outOfScope && !isNewChat) {
        const fullName = getStoredUserName() || "Unknown User";

        await reportOutOfScope({
          userId: fullName,
          question: transcript,
          llmAnswer: data.data.answer,
        });
      }

      // Create/update conversation session synchronously.
      // Skip for proactive starts: there is no user-authored transcript to title the session with;
      // we'll let the first real user turn create/update the session instead.
      if (!isNewChat && userAuthenticated && userId) {
        // Did the user stay in this conversation while the response loaded?
        const stillInTurnConversation = getSessionId() === turnSessionId;
        try {
          await createOrUpdateSession(turnSessionId, userId, transcript);
          if (stillInTurnConversation) {
            setCurrentConversationSessionId(turnSessionId);
            pendingNavigationSessionIdRef.current = turnSessionId;
            navigate(`/chat/${turnSessionId}`);
          }
          loadConversations(0, false);
        } catch (e) {
          console.error("Failed to create/update session", e);
        }
        // Persist the message straight to the audit — the reliable path (same as session
        // creation), independent of the response-service's flaky internal audit hop. Always
        // filed under the turn's own session so it can never land in another conversation.
        // Fire-and-forget so it never blocks the avatar's response.
        saveInteraction({
          convId: data.data?.conversationId,
          inputText: transcript,
          responseText: data.data?.answer,
          sessionId: turnSessionId,
          userId,
          languageUsed: backendLangCode,
          responseTime: (Date.now() - startTime) / 1000,
        }).catch((e) => console.error("Failed to persist interaction to history", e));
      }

      // If the user navigated to a different conversation while this response was loading, the
      // answer belongs to turnSessionId (already persisted above) — do NOT render it into, or
      // play it over, the conversation they're now viewing.
      if (getSessionId() !== turnSessionId) {
        setAvatarLoading(false);
        setIsProcessingResponse(false);
        return;
      }

      if (useAvtrWebRtc) {
        speakViaWebRtc(
          data.data.answer,
          data.data.voiceText,
          data.data.conversationId
        );
      } else if (isPictureMode) {
        getAvatarVideo(
          data.data.answer,
          data.data.voiceText,
          data.data.conversationId
        );
      } else {
        getAudioResponse(
          data.data.answer,
          data.data.voiceText,
          data.data.conversationId
        );
      }
    } catch (error) {
      console.error("❌ Error getting AI response:", error);
      setIsProcessingResponse(false);
      setAvatarLoading(false);
      if (isNewChat) throw error;
    }
  };

  const handleProactiveStart = () => {
    if (avtrInteractionDisabled) return;
    if (proactiveStartedRef.current) return;
    proactiveStartedRef.current = true;
    setShowProactiveOverlay(false);
    getAiResponse("", undefined, { isNewChat: true }).catch(() => {
      proactiveStartedRef.current = false;
      setShowProactiveOverlay(true);
    });
  };

  const resolveVoiceId = (): string | null => {
    // User-picked voice override from personalization → falls through to the avatar's
    // admin-resolved voice (avatar.elevenLabsVoiceId, set at avatar save time using
    // (dialectIsoCode, gender)) when no override is set.
    const override = personalizationForm.avatarVoiceId;
    if (override) return override;
    return (avatar as any)?.elevenLabsVoiceId || null;
  };

  const fetchAudio = async (answer: string) => {
    const currentVoiceId = resolveVoiceId();

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      body: JSON.stringify({ text: answer, voiceId: currentVoiceId }),
    };

    const { REACT_APP_AVATAR_GENERATE_AUDIO }: any = process.env;

    const response = (await fetch(
      REACT_APP_AVATAR_GENERATE_AUDIO,
      requestOptions
    )) as any;

    return response;
  };

  // Must match the sidecar's pinned encode: H.264 main@3.1 + AAC-LC in fragmented MP4.
  const AVTR_MSE_MIME = 'video/mp4; codecs="avc1.4d401f,mp4a.40.2"';

  /**
   * Real-time streaming turn (AVTR-1 WebRTC picture mode). Opens ONE SSE connection to
   * /llm/avatar-speak-stream: the backend streams the LLM answer, feeds ElevenLabs progressively,
   * and pushes audio into the live streamer session — so the avatar starts talking mid-answer. Over
   * this connection we only render the display tokens as they arrive; the avatar's audio + talking
   * state are driven by the live session's audio detector (onSpeaking), exactly like speakViaWebRtc.
   *
   * Returns true if it owned the turn (so the caller must NOT run the legacy path); false only if
   * streaming was unavailable / failed before any token, in which case the caller falls back.
   */
  const tryStreamTurn = async (
    payload: any,
    ctx: {
      transcript: string;
      turnSessionId: string;
      backendLangCode: string;
      startTime: number;
      isNewChat: boolean;
    }
  ): Promise<boolean> => {
    const streamUrl =
      process.env.REACT_APP_AVATAR_SPEAK_STREAM ||
      (process.env.REACT_APP_AVATAR_SPEAK
        ? process.env.REACT_APP_AVATAR_SPEAK.replace(/avatar-speak$/, "avatar-speak-stream")
        : "");
    if (!streamUrl) return false;

    const voiceId = resolveAvtrVoiceId() ?? resolveVoiceId();
    // The backend streaming path doesn't mint a conversationId (history lives on the frontend), so
    // generate one here for the message bubble + audit persistence.
    const conversationId =
      (typeof crypto !== "undefined" && crypto.randomUUID?.()) || `conv-${Date.now()}`;

    let answerAcc = "";
    let started = false; // a token has arrived → the bubble exists and audio is (or will be) playing

    const renderToken = (text: string) => {
      if (!text) return;
      answerAcc += text;
      // Don't bleed this turn's text into a conversation the user switched to mid-stream.
      if (getSessionId() !== ctx.turnSessionId) return;
      if (!started) {
        started = true;
        setMessages((prev: any) => [
          ...prev,
          { sender: "ai", text: answerAcc, conversationId },
        ]);
      } else {
        setMessages((prev: any) =>
          prev.map((m: any) =>
            m.sender === "ai" && m.conversationId === conversationId
              ? { ...m, text: answerAcc }
              : m
          )
        );
      }
    };

    avtrClientRef.current?.unmute(); // ensure avatar voice is audible (user-gesture context)
    avtrSpeakingRef.current = false;
    avtrSpeakAbortRef.current?.abort();
    const speakAbort = new AbortController();
    avtrSpeakAbortRef.current = speakAbort;

    try {
      await streamAvatarSpeak(
        streamUrl,
        { ...payload, voiceId },
        getAuthHeaders(),
        {
          onToken: (t) => renderToken(t?.text ?? ""),
          onStatus: (s) => {
            if (s?.phase) console.log(`[avatar-speak-stream] ${s.phase} @ ${s.tMs ?? "?"}ms`);
          },
          onError: (e) => console.error("[avatar-speak-stream] error event:", e),
        },
        speakAbort.signal
      );
    } catch (err: any) {
      if (err?.name === "AbortError") return true; // superseded by a newer turn / interrupted
      if (!started) {
        // Nothing was shown or played yet → safe for the caller to fall back to the buffered path.
        console.warn("[avatar-speak-stream] setup failed; falling back:", err);
        return false;
      }
      console.error("[avatar-speak-stream] failed mid-stream:", err);
      setAvatarTalking(false);
      setAvatarLoading(false);
      setIsProcessingResponse(false);
      handleUserStoppedSpeaking();
      return true;
    } finally {
      if (avtrSpeakAbortRef.current === speakAbort) avtrSpeakAbortRef.current = null;
    }

    // Finalize history (same side-effects as the legacy WebRTC path), filed under the turn's session.
    if (
      getSessionId() === ctx.turnSessionId &&
      !ctx.isNewChat &&
      userAuthenticated &&
      userId
    ) {
      try {
        await createOrUpdateSession(ctx.turnSessionId, userId, ctx.transcript);
        setCurrentConversationSessionId(ctx.turnSessionId);
        pendingNavigationSessionIdRef.current = ctx.turnSessionId;
        navigate(`/chat/${ctx.turnSessionId}`);
        loadConversations(0, false);
      } catch (e) {
        console.error("Failed to create/update session", e);
      }
      saveInteraction({
        convId: conversationId,
        inputText: ctx.transcript,
        responseText: answerAcc,
        sessionId: ctx.turnSessionId,
        userId,
        languageUsed: ctx.backendLangCode,
        responseTime: (Date.now() - ctx.startTime) / 1000,
      }).catch((e) => console.error("Failed to persist interaction to history", e));
    }

    // Safety net: if her audio never registers shortly after the stream ends (very short/quiet
    // reply, or detection unavailable), don't leave the UI stuck in "thinking".
    setTimeout(() => {
      if (!avtrSpeakingRef.current) {
        setAvatarLoading(false);
        setIsProcessingResponse(false);
      }
    }, 3000);

    return true;
  };

  /**
   * WebRTC picture mode: the avatar is already connected and idling. Just tell the backend to
   * synthesize the answer and push it into the live session (/avatar-speak → streamer /speak).
   * The avatar speaks in the persistent WebRTC video; no MP4 is fetched here.
   */
  const speakViaWebRtc = async (
    answer: string,
    voiceText: string,
    conversationId: string
  ) => {
    const { REACT_APP_AVATAR_SPEAK }: any = process.env;
    try {
      setMessages((prevMessages: any) => [
        ...prevMessages,
        { sender: "ai", text: answer, conversationId },
      ]);
      avtrClientRef.current?.unmute(); // ensure avatar voice is audible (user-gesture context)
      avtrSpeakingRef.current = false;
      // Stay in "thinking" (avatarLoading) until her audio actually starts. avatarTalking is driven
      // by the live remote-audio detector (onSpeaking), NOT by this request's lifecycle — the /speak
      // fetch returns when PCM finished uploading, but she keeps playing for seconds after.

      // Picture avatars: send the ElevenLabs voice resolved for the character's (dialect, gender).
      // The frontend decides the voiceId; the backend just uses whatever we send.
      const voiceId = resolveAvtrVoiceId() ?? resolveVoiceId();
      avtrSpeakAbortRef.current?.abort();
      const speakAbort = new AbortController();
      avtrSpeakAbortRef.current = speakAbort;
      const response = await fetch(REACT_APP_AVATAR_SPEAK, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeaders() },
        body: JSON.stringify({ text: voiceText, voiceId }),
        signal: speakAbort.signal,
      });
      if (avtrSpeakAbortRef.current === speakAbort) {
        avtrSpeakAbortRef.current = null;
      }
      if (!response.ok) {
        throw new Error(`avatar-speak request failed: ${response.status}`);
      }

      // Safety net: if the detector never reports speech shortly after the audio was delivered
      // (e.g. a very short/quiet reply, or detection unavailable), don't leave the UI stuck.
      setTimeout(() => {
        if (!avtrSpeakingRef.current) {
          setAvatarLoading(false);
          setIsProcessingResponse(false);
        }
      }, 3000);
    } catch (error) {
      if ((error as any)?.name === "AbortError") {
        return;
      }
      console.error("❌ Error in speakViaWebRtc:", error);
      setAvatarTalking(false);
      setAvatarLoading(false);
      setReturnedText("");
      setIsProcessingResponse(false);
      handleUserStoppedSpeaking();
    }
  };

  /**
   * Picture mode: stream the AVTR talking-head MP4 from the backend and play it in the picture
   * <video> WHILE it renders. The backend streams fragmented MP4 (render is faster than real
   * time), so we feed fragments into a MediaSource buffer and playback starts ~1s in instead of
   * waiting for the whole clip. Audio + lip-sync are inside the MP4, so this replaces the whole
   * getAudioResponse (MediaSource-audio + AudioAligner + animateSpeech) path. Falls back to a
   * full blob download if MSE can't take our mime.
   */
  const getAvatarVideo = async (
    answer: string,
    voiceText: string,
    conversationId: string
  ) => {
    const { REACT_APP_AVATAR_GENERATE_VIDEO }: any = process.env;
    try {
      setMessages((prevMessages: any) => [
        ...prevMessages,
        { sender: "ai", text: answer, conversationId },
      ]);

      // Picture avatars: send the ElevenLabs voice resolved for the character's (dialect, gender).
      // The frontend decides the voiceId; the backend just uses whatever we send.
      const voiceId = resolveAvtrVoiceId() ?? resolveVoiceId();
      const response = await fetch(REACT_APP_AVATAR_GENERATE_VIDEO, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ text: voiceText, voiceId }),
      });

      if (!response.ok) {
        throw new Error(`Avatar video request failed: ${response.status}`);
      }

      const video = pictureVideoRef.current;
      if (!video) {
        throw new Error("Picture avatar <video> element is not mounted");
      }

      // Release the previous clip's object URL before swapping in the new one.
      if (video.src && video.src.startsWith("blob:")) {
        URL.revokeObjectURL(video.src);
      }

      video.onended = () => {
        setAvatarTalking(false); // crossfades back to the idle layer
        setAvatarLoading(false);
        setReturnedText("");
        setIsProcessingResponse(false);
        if (video.src && video.src.startsWith("blob:")) {
          URL.revokeObjectURL(video.src);
        }
        if (isFloatingChatEnabled) {
          clearMessagesTimeoutRef.current = setTimeout(() => {
            setMessages([]);
            clearMessagesTimeoutRef.current = null;
          }, 2000);
        }
        handleUserStoppedSpeaking();
      };

      const canStream =
        typeof MediaSource !== "undefined" &&
        MediaSource.isTypeSupported(AVTR_MSE_MIME) &&
        !!response.body;

      if (canStream) {
        const mediaSource = new MediaSource();
        video.src = URL.createObjectURL(mediaSource);
        mediaSource.addEventListener(
          "sourceopen",
          async () => {
            const sourceBuffer = mediaSource.addSourceBuffer(AVTR_MSE_MIME);
            const reader = (response.body as ReadableStream<Uint8Array>).getReader();
            try {
              for (;;) {
                const { done, value } = await reader.read();
                if (done) break;
                await new Promise((resolve) => {
                  sourceBuffer.addEventListener("updateend", () => resolve(true), {
                    once: true,
                  });
                  sourceBuffer.appendBuffer(value);
                });
              }
            } catch (streamError) {
              console.error("❌ Avatar video stream interrupted:", streamError);
            } finally {
              // Whatever arrived plays out, then 'ended' fires and cleans up.
              if (mediaSource.readyState === "open") {
                mediaSource.endOfStream();
              }
            }
          },
          { once: true }
        );
      } else {
        const blob = await response.blob();
        video.src = URL.createObjectURL(blob);
      }

      // First fragment decoded → start talking (crossfade handled by avatarTalking opacity).
      await new Promise<void>((resolve, reject) => {
        video.addEventListener("canplay", () => resolve(), { once: true });
        video.addEventListener(
          "error",
          () => reject(new Error("avatar video failed to decode")),
          { once: true }
        );
      });

      if (isFloatingChatEnabled) {
        setShowChatBox(true);
      }
      setAvatarTalking(true);
      setAvatarLoading(false);
      await video.play();
    } catch (error) {
      console.error("❌ Error in getAvatarVideo:", error);
      setAvatarTalking(false);
      setAvatarLoading(false);
      setIsProcessingResponse(false);
      handleUserStoppedSpeaking();
    }
  };

  const getAudioResponse = async (
    answer: string,
    voiceText: string,
    conversationId: string
  ) => {
    try {
      const response = await fetchAudio(voiceText);

      const audio = new Audio();
      audio.preload = "auto";
      setAvatarAudio(audio);
      // Set once the stream's leading declaration line has been read, just before playback starts.
      let audioFormat: string = GLB_AUDIO_FORMAT_MP3;
      let pcmSampleRate = 0;
      let isPcmAudio = false;
      let audioObjectUrl: string | null = null;
      let audioWatchdogTimeout: ReturnType<typeof setTimeout> | null = null;
      let playbackStarted = false;

      alignmentsRef.current = [];
      alignmentsRevisionRef.current += 1;

      const audioAligner = new AudioAligner();
      const audioChunks: Uint8Array[] = [];
      let latestAlignmentRequestId = 0;
      let lastAlignmentStartedAt = 0;
      let alignmentFailureLogged = false;

      const updateAlignmentFromAudio = async (force = false) => {
        if (!audioChunks.length) return;

        const now = performance.now();
        if (!force && now - lastAlignmentStartedAt < AUDIO_ALIGNMENT_THROTTLE_MS) {
          return;
        }

        lastAlignmentStartedAt = now;
        const requestId = ++latestAlignmentRequestId;

        try {
          const audioBase64 = uint8ChunksToBase64(audioChunks);
          const alignmentTranscript = force
            ? voiceText
            : selectTranscriptPrefixForAudio(
              voiceText,
              isPcmAudio
                ? estimatePcm16DurationSeconds(audioChunks, pcmSampleRate)
                : estimateMp3DurationSeconds(audioChunks)
            );

          // Raw PCM goes straight into the aligner — no container to decode, and a partial buffer
          // is still valid input, so the interim (throttled) alignments stay meaningful.
          const result = isPcmAudio
            ? await audioAligner.alignRawPCMBase64(
              audioBase64,
              { sampleRate: pcmSampleRate, encoding: "pcm16" },
              alignmentTranscript
            )
            : await audioAligner.alignBase64(
              `data:${AUDIO_ALIGNMENT_MIME_TYPE};base64,${audioBase64}`,
              alignmentTranscript
            );

          if (requestId !== latestAlignmentRequestId) return;

          const alignment = result?.alignment || result?.normalized_alignment;
          if (alignment) {
            alignmentsRef.current = [alignment];
            alignmentsRevisionRef.current += 1;
            if (force) {
              console.info("[avatar-audio] alignment ready", {
                characters: alignment.characters?.length ?? 0,
              });
            }
          } else if (force) {
            console.warn("[avatar-audio] aligner returned no alignment (no lip-sync)");
          }
        } catch (error) {
          // Interim attempts run every 250ms — report the first failure and the final one only,
          // so a persistent aligner problem is visible without flooding the log.
          if (force || !alignmentFailureLogged) {
            alignmentFailureLogged = true;
            console.warn(
              `[avatar-audio] alignment failed${force ? " (final)" : ""}:`,
              (error as Error)?.message || error
            );
          }
        }
      };

      setMessages((prevMessages: any) => [
        ...prevMessages,
        { sender: "ai", text: answer, conversationId },
      ]);
      console.info("[avatar-audio] response received", {
        iosWebKit: isLikelyIosWebKit(),
      });

      const clearAudioWatchdog = () => {
        if (audioWatchdogTimeout) {
          clearTimeout(audioWatchdogTimeout);
          audioWatchdogTimeout = null;
        }
      };

      const startAudioWatchdog = (phase: string) => {
        clearAudioWatchdog();
        audioWatchdogTimeout = setTimeout(() => {
          if (playbackStarted) return;
          console.warn("[avatar-audio] playback watchdog expired", { phase });
          setAvatarTalking(false);
          setAvatarLoading(false);
          setIsProcessingResponse(false);
          handleUserStoppedSpeaking();
        }, 4500);
      };

      audio.addEventListener(
        "playing",
        () => {
          playbackStarted = true;
          clearAudioWatchdog();
          console.info("[avatar-audio] playing event fired");
        },
        { once: true }
      );

      audio.addEventListener(
        "loadedmetadata",
        () => {
          console.info("[avatar-audio] loadedmetadata fired", {
            duration: Number.isFinite(audio.duration) ? audio.duration : null,
          });
        },
        { once: true }
      );

      // "Plays for a second then stops" is buffer starvation, not an error — the element fires
      // waiting/stalled and sits there. Make that visible, with where it got to.
      (["waiting", "stalled", "suspend", "pause"] as const).forEach((eventName) => {
        audio.addEventListener(eventName, () => {
          console.warn(`[avatar-audio] ${eventName}`, {
            currentTime: Number(audio.currentTime.toFixed(2)),
            buffered:
              audio.buffered.length > 0
                ? Number(audio.buffered.end(audio.buffered.length - 1).toFixed(2))
                : 0,
            readyState: audio.readyState,
          });
        });
      });

      audio.addEventListener(
        "error",
        () => {
          clearAudioWatchdog();
          console.error("[avatar-audio] audio element error", {
            code: audio.error?.code,
            message: audio.error?.message,
          });
          setAvatarTalking(false);
          setAvatarLoading(false);
          setIsProcessingResponse(false);
          handleUserStoppedSpeaking();
        },
        { once: true }
      );

      audio.addEventListener(
        "canplay",
        async () => {
          try {
            console.info("[avatar-audio] canplay fired");
            if (videoRef.current && hasPlaylist && allVideos.length > 0) {
              if (!videoRef.current.paused) {
                videoRef.current.pause();
              }
            }

            if (isFloatingChatEnabled) {
              setShowChatBox(true);
            }

            {
              const profile = SCHEME_PROFILES[morphScheme];
              handleAnimationTransition(avatarActions, profile.clips.talk, {
                alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
              });
            }
            setAvatarTalking(true);
            setAvatarLoading(false);

            console.info("[avatar-audio] attempting playback");
            await audio.play();
            console.info("[avatar-audio] playback started");
            animateSpeech(audio);
          } catch (error) {
            // NotAllowedError here = the browser refused autoplay because the user gesture that
            // started the turn no longer covers this call (typical on iOS).
            console.error(
              `❌ Error playing audio: ${(error as Error)?.name || "Error"} — ${(error as Error)?.message || error
              }`
            );
            stopAudio();
            setIsProcessingResponse(false);
            handleUserStoppedSpeaking();
          }
        },
        { once: true }
      );

      audio.addEventListener(
        "ended",
        () => {
          if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
          }
          setAvatarTalking(false);
          setAvatarLoading(false);
          setReturnedText("");
          setIsProcessingResponse(false);
          resetSpeech();
          setAvatarAudio(null);
          clearAudioWatchdog();
          if (audioObjectUrl) {
            URL.revokeObjectURL(audioObjectUrl);
            audioObjectUrl = null;
          }
          {
            const profile = SCHEME_PROFILES[morphScheme];
            handleAnimationTransition(avatarActions, profile.clips.idle, {
              alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
            });
          }

          if (isFloatingChatEnabled) {
            clearMessagesTimeoutRef.current = setTimeout(() => {
              setMessages([]);
              clearMessagesTimeoutRef.current = null;
            }, 2000);
          }

          handleUserStoppedSpeaking();
        },
        { once: true }
      );

      // Read ONE line ahead to learn what the stream is carrying. A PCM provider announces itself
      // with a leading `{"audio_format":"pcm16","sample_rate":16000}` line; ElevenLabs sends audio
      // straight away and is therefore mp3 by omission. Peeking costs nothing extra — it is the
      // same first byte playback was going to wait for — and it means the server can be rolled back
      // between providers without a matching frontend deploy.
      const eventStream = readNDJSONStream(response.body) as AsyncGenerator<any>;
      let pendingEvent: any = null;
      const firstRead = await eventStream.next();
      if (!firstRead.done) {
        const first = firstRead.value;
        if (first?.audio_format) {
          audioFormat = String(first.audio_format);
          pcmSampleRate = Number(first.sample_rate) || 0;
        } else {
          pendingEvent = first; // already audio — no declaration line on this stream
        }
      }

      isPcmAudio = audioFormat === GLB_AUDIO_FORMAT_PCM16;
      if (isPcmAudio && !pcmSampleRate) {
        console.warn("[avatar-audio] pcm16 stream declared no sample_rate; assuming", {
          sampleRate: GLB_PCM_FALLBACK_SAMPLE_RATE,
        });
        pcmSampleRate = GLB_PCM_FALLBACK_SAMPLE_RATE;
      }
      if (!isPcmAudio && audioFormat !== GLB_AUDIO_FORMAT_MP3) {
        console.warn("[avatar-audio] unrecognised audio_format; treating as mp3", { audioFormat });
      }

      // MediaSource only accepts containerised codecs — there is no MSE path for raw PCM or WAV in
      // any browser. So PCM always takes the buffered branch: the same one iOS and mobile already
      // use, at the cost of first-word latency on desktop.
      const shouldStreamAudio =
        !isPcmAudio && canUseStreamedAudioMse() && !!response.body;
      console.info("[avatar-audio] stream format", {
        audioFormat,
        sampleRate: isPcmAudio ? pcmSampleRate : null,
        mode: shouldStreamAudio ? "streamed-mse" : "buffered-fallback",
      });

      // Pull the NDJSON TTS stream, feeding every audio chunk to the aligner (so visemes stay in
      // step with what's audible) and to `onChunk` for playback.
      const consumeAudioStream = async (
        onChunk: (bytes: Uint8Array) => Promise<void> | void
      ) => {
        let totalBytes = 0;
        const handleEvent = async (event: any) => {
          if (!event?.audio_base64) return;
          const binaryData = Uint8Array.from(atob(event.audio_base64), (c) =>
            c.charCodeAt(0)
          );
          audioChunks.push(binaryData);
          totalBytes += binaryData.length;
          void updateAlignmentFromAudio();
          await onChunk(binaryData);
        };

        if (pendingEvent) {
          const buffered = pendingEvent;
          pendingEvent = null;
          await handleEvent(buffered);
        }
        for await (const event of eventStream) {
          await handleEvent(event);
        }
        console.info("[avatar-audio] stream complete", {
          chunks: audioChunks.length,
          kb: Math.round(totalBytes / 1024),
        });
        await updateAlignmentFromAudio(true);
      };

      if (shouldStreamAudio) {
        // Desktop/Android: append fragments into a MediaSource so playback starts on the first
        // chunk instead of after the whole answer is synthesized.
        const mediaSource = new MediaSource();
        audioObjectUrl = URL.createObjectURL(mediaSource);
        audio.src = audioObjectUrl;
        startAudioWatchdog("streamed-mse");

        mediaSource.addEventListener(
          "sourceopen",
          async () => {
            const sourceBuffer = mediaSource.addSourceBuffer(
              AUDIO_ALIGNMENT_MIME_TYPE
            );
            try {
              await consumeAudioStream(
                (bytes) =>
                  new Promise<void>((resolve) => {
                    sourceBuffer.addEventListener(
                      "updateend",
                      () => resolve(),
                      { once: true }
                    );
                    sourceBuffer.appendBuffer(bytes as BufferSource);
                  })
              );
            } catch (streamError) {
              console.error("❌ Audio stream interrupted:", streamError);
            } finally {
              if (mediaSource.readyState === "open") {
                mediaSource.endOfStream();
              }
            }
          },
          { once: true }
        );
      } else {
        // Buffered branch: either iOS WebKit (which can't MSE `audio/mpeg`) or a raw-PCM stream
        // (which no browser can MSE at all). Buffer the whole answer and hand the element a plain
        // blob URL — a WAV container for PCM, the mp3 bytes as-is otherwise. The watchdog only
        // starts once the source is attached, or a long synthesis would trip it mid-download.
        await consumeAudioStream(() => {});
        const audioBlob = isPcmAudio
          ? buildWavBlob(audioChunks, pcmSampleRate)
          : new Blob(audioChunks as BlobPart[], {
            type: AUDIO_ALIGNMENT_MIME_TYPE,
          });
        audioObjectUrl = URL.createObjectURL(audioBlob);
        audio.src = audioObjectUrl;
        audio.load();
        startAudioWatchdog("buffered-fallback");
      }
    } catch (error) {
      console.error("❌ Error in getAudioResponse:", error);
      setIsProcessingResponse(false);
      stopAudio();
    }
  };

  if (returnedText && !avatarTalking && avatarAudio) {
    setAvatarTalking(true);
    setAvatarLoading(false);

    const onAudioStart = async () => {
      try {
        const profile = SCHEME_PROFILES[morphScheme];
        handleAnimationTransition(avatarActions, profile.clips.talkVariant, {
          alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
        });
      } catch {
        stopAudio();
      }
    };

    avatarAudio.addEventListener("playing", onAudioStart, { once: true });

    avatarAudio.addEventListener("ended", () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      setAvatarTalking(false);
      setAvatarLoading(false);
      setReturnedText("");
      resetSpeech();
      setAvatarAudio(null);
      {
        const profile = SCHEME_PROFILES[morphScheme];
        handleAnimationTransition(avatarActions, profile.clips.idle, {
          alsoPlay: profile.enableBlinking ? [profile.blinkingClip] : undefined,
        });
      }
    });
  }

  const handleMessageSend = (message: string) => {
    if (avtrInteractionDisabled) return;
    setShowChatBox(true);
    setIsChatSidebarOpen(true);
    getAiResponse(message);
  };

  const renderLayout = () => {
    if (channelBlocked) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", height: "100vh", textAlign: "center",
          backgroundColor: "#f5f5f5", fontFamily: "sans-serif", padding: "2rem"
        }}>
          <h2 style={{ marginBottom: "1rem", color: "#333" }}>{uiCopy.accessRestrictedTitle}</h2>
          <p style={{ color: "#666", maxWidth: "400px" }}>
            {uiCopy.accessRestrictedBody}
          </p>
        </div>
      );
    }

    if (hasPlaylist === null || loading) {
      return <Preloader />;
    }

    if (!hasPlaylist) {
      const backgroundStyles =
        parseInt(avatarTheme.linkTypeId) === 1 && avatarThemeAssetBlob
          ? {
            background: `url(${avatarThemeAssetBlob}) no-repeat center center / cover`,
          }
          : {};

      return (
        <div className="fullscreen-canvas" style={backgroundStyles}>
          {!glbReady && <Preloader />}

          {avatarTheme.linkTypeId === 2 && avatarThemeAssetBlob && (
            <video autoPlay loop muted className="video-player">
              <source src={avatarThemeAssetBlob} type="video/mp4" />
            </video>
          )}

          {!tapToTalk && autoStart && isAvatarListeningIndicator && (
            <>
              <div className="avatar-listening-indicator">
                {uiCopy.avatarListening}
              </div>
            </>
          )}

          {settingsLoaded && !isReadOnly && (
            <Dictaphone
              avatarActions={avatarActions}
              avatarTalking={avatarTalking}
              stopAudio={stopAudio}
              avatarLoading={avatarLoading}
              languageCode={
                personalizationForm.speakerLanguage === AUTO_DETECT_LANGUAGE
                  ? undefined
                  : personalizationForm.speakerLanguage || language
              }
              onTranscriptChange={(text, _reset, detectedLang) => {
                setShowChatBox(true);
                setIsChatSidebarOpen(true);
                getAiResponse(text, detectedLang);
              }}
              onListeningStart={handleUserStartedSpeaking}
              onListeningStop={handleUserStoppedSpeaking}
              tapToTalk={tapToTalk}
              autoStart={autoStart}
              hideMic={hideMic}
              sessionResetCounter={sessionResetCounter}
              sttMode={personalizationForm.speakerLanguage || language}
              systemLanguage={systemLanguage}
              disabled={avtrInteractionDisabled}
            />
          )}

          {renderLiveChatPanel()}

          {isPictureMode ? (
            pictureAvatarEl
          ) : (
            <Canvas
              camera={{ fov: 40 }}
              gl={{ toneMapping: THREE.LinearToneMapping }}
            >
              <CameraUpdater fov={avatar?.cameraFov ?? avatarSettings?.cameraFov} />
              {renderAvatar}
            </Canvas>
          )}

          {videoStatus !== "playing" && (
            <div className="video-status-message">
              {videoStatus === "paused"
                ? uiCopy.videoPaused
                : uiCopy.videoResumed}
            </div>
          )}
        </div>
      );
    }

    if (!everythingLoaded || loading) {
      return <Preloader />;
    }

    const backgroundStyles =
      parseInt(avatarTheme.linkTypeId) === 1
        ? { background: "transparent" }
        : {};

    return (
      <div className="full-screen-layout">
        {!glbReady && <Preloader />}
        <div className="video-section">
          {allVideos.length > 0 ? (
            <video
              key={allVideos[currentVideoIndex]?.url}
              ref={videoRef}
              className="playlist-video"
              playsInline
              autoPlay
            >
              <source
                src={allVideos[currentVideoIndex]?.url}
                type="video/mp4"
              />
            </video>
          ) : (
            <div className="playlist-placeholder"></div>
          )}

          {videoStatus !== "playing" && (
            <div className="video-status-message">
              {videoStatus === "paused"
                ? uiCopy.videoPaused
                : uiCopy.videoResumed}
            </div>
          )}
        </div>

        <div className="avatar-section" style={backgroundStyles}>
          {avatarTheme.linkTypeId === 2 && avatarThemeAssetBlob && (
            <video autoPlay loop muted className="background-video">
              <source src={avatarThemeAssetBlob} type="video/mp4" />
            </video>
          )}

          <div className="avatar-canvas-container">
            {!tapToTalk && autoStart && isAvatarListeningIndicator && (
              <div className="avatar-listening-indicator">
                {uiCopy.avatarListening}
              </div>
            )}

            {settingsLoaded && !isReadOnly && (
              <Dictaphone
                avatarActions={avatarActions}
                avatarTalking={avatarTalking}
                stopAudio={stopAudio}
                avatarLoading={avatarLoading}
                languageCode={
                  personalizationForm.speakerLanguage === AUTO_DETECT_LANGUAGE
                    ? undefined
                    : personalizationForm.speakerLanguage || language
                }
                onTranscriptChange={(text, _reset, detectedLang) => {
                  setShowChatBox(true);
                  setIsChatSidebarOpen(true);
                  getAiResponse(text, detectedLang);
                }}
                onListeningStart={handleUserStartedSpeaking}
                onListeningStop={handleUserStoppedSpeaking}
                tapToTalk={tapToTalk}
                autoStart={autoStart}
                hideMic={hideMic}
                sessionResetCounter={sessionResetCounter}
                sttMode={personalizationForm.speakerLanguage || language}
                systemLanguage={systemLanguage}
                disabled={avtrInteractionDisabled}
              />
            )}

            {isPictureMode ? (
              pictureAvatarEl
            ) : (
              <Canvas
                camera={{ fov: 40 }}
                gl={{ toneMapping: THREE.LinearToneMapping }}
              >
                <CameraUpdater fov={avatar?.cameraFov ?? avatarSettings?.cameraFov} />
                {renderAvatar}
              </Canvas>
            )}
          </div>

        </div>

        {renderLiveChatPanel()}
      </div>
    );
  };

  return (
    <Suspense fallback={<Preloader />}>
      {showVisemeDebug && morphTarget && (
        <VisemeDebugPanel morphTarget={morphTarget} morphScheme={morphScheme} />
      )}
      {userAuthenticated && (
        <ConversationSidebar
          conversations={conversations}
          currentSessionId={currentConversationSessionId}
          onSelectConversation={handleSelectConversation}
          onNewConversation={handleNewConversation}
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          onOpen={() => setIsSidebarOpen(true)}
          hasMore={hasMoreConversations}
          isLoadingMore={isLoadingMoreConversations}
          onLoadMore={() => loadConversations(conversationPage + 1)}
          loading={loadingConversations}
          onConversationDeleted={handleConversationDeleted}
          onConversationRenamed={handleConversationRenamed}
          systemLanguage={systemLanguage}
          isMobile={shouldDockSidebarBottom}
        />
      )}
      {/* TOP NAVBAR */}
      <header
        className="fixed top-0 left-0 right-0 h-16 bg-white flex items-center"
        style={{
          zIndex: 9,
          paddingLeft: "16px",
          paddingRight: "16px",
          borderBottom: '1px solid #e0e3ff',
        }}
      >
        {/* Left: ABG Logo */}
        <div className="flex-shrink-0">
          <img src={abgLogo} alt="ABG" className="h-[28px] w-auto" />
        </div>

        <div className="flex-1 text-center min-w-0 px-3">
          <span
            className="text-[#1a1a2e] font-semibold block"
            style={{
              fontSize: isMobileLayout ? "clamp(0.9rem, 3.8vw, 1rem)" : undefined,
              lineHeight: isMobileLayout ? 1.2 : undefined,
              whiteSpace: isMobileLayout ? "normal" : undefined,
            }}
          >
            {uiCopy.heroTitle}
          </span>
        </div>

        {/* Center: Title */}
        <div className="hidden">
          <span className="text-[#1a1a2e] font-semibold text-base">
            {isReadOnly ? (systemLanguage === "ar" ? "محادثة مشتركة (للقراءة فقط)" : "Shared Conversation (Read Only)") : uiCopy.heroTitle}
          </span>
        </div>

        {/* Right: Settings + Language + Auth */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Settings are per-account preferences: only offered to a signed-in user. */}
          {userAuthenticated && (
            <button
              onClick={() => setShowSettingsModal(true)}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#80868E] transition-colors"
              title={uiCopy.settings}
            >
              <Settings size={20} />
            </button>
          )}

          {false && <button
            onClick={() => {
              const newLang = systemLanguage === "ar" ? "en" : "ar";
              setPersonalizationForm((prev: any) => ({
                ...prev,
                systemLanguage: newLang,
              }));
            }}
            className="px-3 py-1.5 rounded-lg hover:bg-gray-100 text-[#80868E] font-semibold text-sm transition-colors"
            title={systemLanguage === "ar" ? "English" : "العربية"}
          >
            {systemLanguage === "ar" ? "EN" : "AR"}
          </button>}

          {userAuthenticated ? (
            <button
              onClick={() => {
                clearAuthStorage();
                window.location.href = "/";
              }}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#80868E] transition-colors"
              title={uiCopy.logout}
            >
              <LogOut size={20} />
            </button>
          ) : (
            <button
              onClick={() => { window.location.href = "/login"; }}
              className="p-2 rounded-lg hover:bg-gray-100 text-[#80868E] transition-colors"
              title={uiCopy.signIn}
            >
              <LogIn size={20} />
            </button>
          )}
        </div>
      </header>

      {/* MAIN CONTENT */}
      <div
        style={{
          marginTop: '64px',
          height: 'calc(100dvh - 64px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {renderLayout()}
      </div>
      <PersonalizationModal
        open={userAuthenticated && showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        value={personalizationForm}
        onSave={handleSavePersonalization}
        avatarOptions={avatarOptions}
        systemLanguage={systemLanguage}
      />
      <ProactiveOverlay
        open={showProactiveOverlay}
        disabled={isProcessingResponse || avtrInteractionDisabled}
        onStart={handleProactiveStart}
        systemLanguage={systemLanguage}
      />
      <FaqButton />
      <div
        style={{
          position: "fixed",
          bottom: isMobileLayout ? "112px" : "16px",
          left: 0,
          right: 0,
          textAlign: "center",
          zIndex: 99998,
          pointerEvents: isBottomChatSheetOpen ? "none" : "none",
          opacity: isBottomChatSheetOpen ? 0 : 1,
          visibility: isBottomChatSheetOpen ? "hidden" : "visible",
          padding: isMobileLayout ? "0 14px" : "0 16px",
          transition: "opacity 180ms ease",
        }}
      >
        <p
          style={{
            display: isMobileLayout ? "flex" : "inline-flex",
            flexDirection: isMobileLayout ? "column" : "row",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            columnGap: "8px",
            rowGap: isMobileLayout ? "6px" : "4px",
            margin: 0,
            fontSize: isMobileLayout ? "12px" : "13px",
            lineHeight: isMobileLayout ? 1.35 : 1.4,
            color: "#9ca3af",
            pointerEvents: "auto",
            maxWidth: isMobileLayout ? "min(100%, 260px)" : "unset",
            marginInline: "auto",
          }}
        >
          <span>
            {systemLanguage === "ar"
              ? "جميع الإجابات تستند إلى مقالات رسمية من مركز معرفة Mynd."
              : "All answers are based on official Mynd Knowledge Center articles."}
          </span>
          {feedbackSubmitted ? (
            <span style={{ color: "#6b7280", fontStyle: "italic" }}>
              {systemLanguage === "ar" ? "تم إرسال الملاحظات" : "Feedback submitted"}
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setShowFeedbackModal(true)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: 500,
                color: "#33f",
                padding: 0,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "underline"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
            >
              {systemLanguage === "ar" ? "شارك ملاحظاتك" : "Share your feedback"}
            </button>
          )}
        </p>
      </div>
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        systemLanguage={systemLanguage}
        onSubmit={async (rating, comment) => {
          await submitUserExperience(rating, comment);
          setFeedbackSubmitted(true);
          try {
            const sessionId = getSessionId();
            if (sessionId) {
              localStorage.setItem(`feedback_${sessionId}`, "true");
            }
          } catch {}
        }}
      />
    </Suspense>
  );
}
